// src/WorldCupBracket.jsx
//
// Full-screen prediction UI for World Cup 2026 bracket-pool leagues.
// Launched when the user taps "Make a Pick" inside a league with
// format === "bracket_pool".
//
// PHASE 2A (this file):
//   ✓ Group stage tap-to-rank (12 groups × 4 placements)
//   ✓ Predictions Firestore plumbing (load + autosave)
//   ✓ Auto-routing based on completion status
//   ✓ Hidden-from-others (writes to private subcollection)
//   ☐ Knockout bracket  (placeholder — Phase 2B)
//   ☐ Tiebreaker        (placeholder — Phase 2B)
//   ☐ Golden boot       (placeholder — Phase 2B)
//   ☐ Read-only summary (placeholder — Phase 2B)

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import app from "./firebase";
import {
  WORLD_CUP_2026,
  TEAMS,
  GROUPS,
  GROUP_LETTERS,
  KNOCKOUT_ROUNDS,
  KNOCKOUT_SLOTS,
  KNOCKOUT_ROUND_LABELS,
  KNOCKOUT_MATCHUPS,
  SCORING,
  predictionsOpen,
  predictionsWritable,
  knockoutPicksOpen,
  getRoundUnlockStatus,
  anyKnockoutUnlocked,
  getMatchTeams,
  formatPositionLabel,
  isThirdPlaceCluster,
} from "./worldCup2026Config";
import {
  subscribeToWorldCupLiveStandings,
  useWorldCupLiveStandings,
  computeGroupStageScore,
  computeKnockoutScore,
  hasAnyLiveData,
  hasAnyKnockoutData,
  formatLastUpdatedAgo,
} from "./worldCup2026Scoring";

// ── Theme — kept in sync with TorchApp.jsx + WorldCupBanner.jsx ──────────
const T = {
  bg:        "#08080A",
  bgSheet:   "#0A0A0F",
  card:      "#0E0E12",
  cardAlt:   "#15151B",
  border:    "rgba(255,255,255,0.06)",
  borderHi:  "rgba(255,255,255,0.16)",
  borderTeal:"rgba(20,184,166,0.30)",
  ink:       "#F4F4F6",
  inkSec:    "rgba(244,244,246,0.62)",
  inkMuted:  "rgba(244,244,246,0.36)",
  inkFaint:  "rgba(244,244,246,0.16)",
  gold:      "#E5B459",
  goldDim:   "rgba(229,180,89,0.16)",
  silver:    "#C7CCD1",
  silverDim: "rgba(199,204,209,0.12)",
  bronze:    "#CD7F32",
  bronzeDim: "rgba(205,127,50,0.14)",
  teal:      "#14B8A6",
  tealDeep:  "#0D9488",
  tealDim:   "rgba(20,184,166,0.12)",
  green:     "#4DA672",
};
const FD = "'Roboto', -apple-system, sans-serif";
const FB = "'Roboto', -apple-system, 'Segoe UI', sans-serif";
const FM = "'Roboto Mono', 'SF Mono', 'Fira Code', monospace";

// ── Cross-platform flag rendering ─────────────────────────────────────────
// Mirrors the helper in TorchApp.jsx — kept duplicated here so this file
// stays self-contained (no cross-file import for a tiny utility). Country
// flag emojis render natively on iOS/macOS/Android but fall back to text
// ("MX", "BR") on most Windows browsers. flagcdn.com serves SVG flag images
// that render consistently everywhere.
const FLAG_ISO_OVERRIDES = { ENG: "gb-eng", SCO: "gb-sct", WAL: "gb-wls", NIR: "gb-nir" };
function teamFlagSrc(team) {
  if (!team) return null;
  if (FLAG_ISO_OVERRIDES[team.abbr]) {
    return `https://flagcdn.com/h20/${FLAG_ISO_OVERRIDES[team.abbr]}.png`;
  }
  if (team.flag) {
    try {
      const cps = [...team.flag].map(c => c.codePointAt(0));
      if (cps.length === 2 && cps.every(cp => cp >= 0x1F1E6 && cp <= 0x1F1FF)) {
        const iso = cps.map(cp => String.fromCharCode(cp - 0x1F1E6 + 65)).join("").toLowerCase();
        return `https://flagcdn.com/h20/${iso}.png`;
      }
    } catch {}
  }
  return null;
}
function TeamFlag({ team, size = 16, style = {} }) {
  const src = teamFlagSrc(team);
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      width={Math.round(size * 4 / 3)}
      height={size}
      loading="lazy"
      decoding="async"
      style={{
        display: "inline-block", verticalAlign: "middle",
        borderRadius: 2, marginRight: 5,
        ...style,
      }}
    />
  );
}

// ── Empty predictions skeleton ──────────────────────────────────────────
const emptyPredictions = () => ({
  groups: {},
  knockouts: { R32: {}, R16: {}, QF: {}, SF: {}, FINAL: {} },
  goldenBoot: null,
  totalGoals: null,
  submittedAt: null,
  lastUpdatedAt: null,
  isLocked: false,
});

// ── Progress helpers ────────────────────────────────────────────────────
function groupStageProgress(predictions) {
  const completed = GROUP_LETTERS.filter((L) => {
    const r = predictions?.groups?.[L];
    return Array.isArray(r) && r.length === 4;
  }).length;
  return { completed, total: 12, pct: completed / 12 };
}

// ── Knockout bracket derivation ─────────────────────────────────────────
// The bracket is COMPUTED from the user's group predictions, not stored
// independently. This means changing a group pick automatically updates
// every downstream matchup, and stored knockout picks for stale matchups
// (teams no longer in the matchup) gracefully read as "unpicked".
//
// Format: 32 teams → R32 → R16 → QF → SF → Final.
//   - 12 group winners      (1st place from each of A-L)
//   - 12 runners-up         (2nd place from each of A-L)
//   - 8 best thirds         (3rd place from groups A-H, auto-selected)
//
// v1 simplification: best thirds = groups A-H thirds. Allowing the user
// to pick which 8 of 12 thirds advance is a nice-to-have for v2.
//
// All pairings are cross-group (no same-group rematches in R32).

const ROUND_ORDER = ["R32", "R16", "QF", "SF", "FINAL"];
const ROUND_SLOTS = { R32: 16, R16: 8, QF: 4, SF: 2, FINAL: 1 };
const ROUND_LABELS = {
  R32:   "Round of 32",
  R16:   "Round of 16",
  QF:    "Quarterfinals",
  SF:    "Semifinals",
  FINAL: "The Final",
};
const ROUND_PTS = {
  R32:   SCORING.R32_WINNER,
  R16:   SCORING.R16_WINNER,
  QF:    SCORING.QF_WINNER,
  SF:    SCORING.SF_WINNER,
  FINAL: SCORING.CHAMPION,
};

// ── Phase 1 knockout model ─────────────────────────────────────────────
// Pre-tournament, knockouts are LOCKED. The user picks groups, golden boot,
// and the tiebreaker — then commits, and waits for kickoff. Each knockout
// round opens up date-by-date as the previous round concludes (see
// KNOCKOUT_UNLOCK_DATES in worldCup2026Config).
//
// We deliberately threw out the v1 "predicted matchup" logic where the
// app derived R32-Final pairings from group picks. That created the false
// expectation that all 31 KO winners could be locked in today — it can't.
// In real bracket pools, you pick winners against the ACTUAL matchups as
// the tournament unfolds. Phase 2 wires this to a live sports API; for
// now we gate at the round level and surface lock-state placeholders.

// Lightweight team-code validator. Replaces the old `validWinner(matchup,...)`
// check, since we no longer derive matchups locally.
function isValidTeamCode(code) {
  return !!(code && typeof code === "string" && TEAMS[code]);
}

function goldenBootDone(predictions) {
  return typeof predictions?.goldenBoot === "string" && predictions.goldenBoot.trim().length > 0;
}
function tiebreakerDone(predictions) {
  const v = Number(predictions?.totalGoals);
  return Number.isFinite(v) && v > 0;
}

// Phase-aware knockout progress. Only counts picks against rounds that are
// currently UNLOCKED — pre-tournament returns { completed:0, total:0, pct:1 }
// (no work to do yet, treat as "done"). As rounds unlock, the denominator
// grows and the user has more picks to make.
// Set of "<round>:<slot>" keys that already finished (have a winnerCode in the
// live doc). A match that kicked off before its round was pickable — e.g. an
// R32 game played on unlock day — can't be picked by anyone, so it must NOT
// count against a user's completion. Without this, a single early match leaves
// every user stuck at 15/16 forever with no way to finish the round.
function finishedSlotSet(liveStandings) {
  const set = new Set();
  const ko = liveStandings?.knockouts;
  if (!ko || typeof ko !== "object") return set;
  for (const r of ROUND_ORDER) {
    const arr = ko[r];
    if (!Array.isArray(arr)) continue;
    for (const m of arr) {
      if (m && m.matchSlot && m.winnerCode) set.add(`${r}:${m.matchSlot}`);
    }
  }
  return set;
}

function knockoutProgress(predictions, now = new Date(), liveStandings = null) {
  let completed = 0, total = 0;
  const finished = finishedSlotSet(liveStandings);
  for (const r of ROUND_ORDER) {
    if (!getRoundUnlockStatus(r, now).isUnlocked) continue;
    const raw = predictions?.knockouts?.[r];
    const storedObj = (raw && typeof raw === "object") ? raw : {};
    // Required = every slot in the round EXCEPT ones that already finished and
    // the user never picked (they had no chance). A finished match the user DID
    // pick in time still counts — they earned/lost the points fairly.
    for (const slotDef of (KNOCKOUT_MATCHUPS[r] || [])) {
      const slot = slotDef.slot;
      const userPicked = isValidTeamCode(storedObj[slot]);
      const isFinished = finished.has(`${r}:${slot}`);
      if (isFinished && !userPicked) continue; // not required — skip
      total += 1;
      if (userPicked) completed++;
    }
  }
  return {
    completed,
    total,
    pct: total === 0 ? 1 : completed / total,
  };
}

// Overall progress — phase-aware. Pre-tournament: 12 groups + boot + tiebreaker
// (14 items). After R32 unlocks: 14 + 16 = 30 items. Etc. Pct hits 1 when
// every CURRENTLY-LOCKABLE pick is locked.
function overallProgress(predictions, now = new Date(), liveStandings = null) {
  const g  = groupStageProgress(predictions);
  const k  = knockoutProgress(predictions, now, liveStandings);
  const gb = goldenBootDone(predictions) ? 1 : 0;
  const tb = tiebreakerDone(predictions) ? 1 : 0;
  const completed = g.completed + k.completed + gb + tb;
  const total = g.total + k.total + 2;
  return { completed, total, pct: total > 0 ? completed / total : 0 };
}


// ── Firestore I/O ───────────────────────────────────────────────────────
async function loadPredictions(leagueId, userId) {
  if (!leagueId || !userId) return null;
  try {
    const { doc: fDoc, getDoc, getFirestore } =
      await import("firebase/firestore");
    const fdb = getFirestore(app);
    const ref = fDoc(fdb, "leagues", leagueId, "predictions", userId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return emptyPredictions();
    return { ...emptyPredictions(), ...snap.data() };
  } catch (e) {
    console.error("[WC2026] loadPredictions failed:", e);
    return null;
  }
}

// ── Client-only flags to strip before saving to Firestore ──────────────
// These are runtime-only (e.g. UI hint state) and shouldn't pollute the
// persisted predictions doc.
const CLIENT_ONLY_KEYS = ["__seenComplete"];
function sanitizeForSave(predictions) {
  const out = { ...predictions };
  for (const k of CLIENT_ONLY_KEYS) delete out[k];
  return out;
}

async function savePredictions(leagueId, userId, username, predictions) {
  if (!leagueId || !userId) return false;
  // ⚠️ Lock gate — short-circuit only when NOTHING is writable. Pre-kickoff the
  // whole bracket is editable; post-kickoff the group stage is frozen but
  // knockout rounds open progressively, so writes must still go through for
  // them. The Firestore rule is the authoritative gate (it rejects any change
  // to the frozen group/boot/tiebreaker fields post-kickoff); this client
  // check just avoids a guaranteed-fail round-trip when even knockouts are
  // still locked (i.e. before R32 opens).
  if (!predictionsWritable()) {
    console.info("[WC2026] savePredictions blocked -- nothing writable yet");
    return false;
  }
  try {
    const { doc: fDoc, setDoc, getFirestore, serverTimestamp } =
      await import("firebase/firestore");
    const fdb = getFirestore(app);
    const ref = fDoc(fdb, "leagues", leagueId, "predictions", userId);
    const payload = {
      ...sanitizeForSave(predictions),
      userId,
      username: username || "Anonymous",
      lastUpdatedAt: serverTimestamp(),
    };
    await setDoc(ref, payload, { merge: true });
    return true;
  } catch (e) {
    console.error("[WC2026] savePredictions failed:", e);
    return false;
  }
}

// ── Tiny inline trophy mark ─────────────────────────────────────────────
const TrophyMark = ({ size = 20 }) => (
  <svg
    viewBox="0 0 32 32"
    width={size}
    height={size}
    aria-hidden="true"
    style={{ display: "block", flexShrink: 0 }}
  >
    <defs>
      <linearGradient id="wcBracketGold" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F2D479" />
        <stop offset="45%" stopColor="#E5B459" />
        <stop offset="100%" stopColor="#A57723" />
      </linearGradient>
    </defs>
    <path d="M9 5 H23 V12 Q23 17 16 17 Q9 17 9 12 Z" fill="url(#wcBracketGold)" />
    <path d="M11 6 H13.5 V11 Q13.5 12.5 12.25 12.5 Q11 12.5 11 11 Z" fill="#FFE9A8" opacity="0.45" />
    <path d="M9 7 Q4 7 4 10.5 Q4 13 9 13" fill="none" stroke="url(#wcBracketGold)" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M23 7 Q28 7 28 10.5 Q28 13 23 13" fill="none" stroke="url(#wcBracketGold)" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="14.5" y="17" width="3" height="4" fill="url(#wcBracketGold)" />
    <rect x="11" y="21" width="10" height="2" rx="0.5" fill="url(#wcBracketGold)" />
    <rect x="9" y="23.5" width="14" height="2.5" rx="0.5" fill="url(#wcBracketGold)" />
  </svg>
);

// Light haptic helper (no-op when unavailable). Mirrors TorchApp pattern.
const tap = () => { try { window.navigator.vibrate?.(8); } catch {} };

// ── Rank badge — colored chip showing 1/2/3/4 ──────────────────────────
function RankBadge({ rank, correctness }) {
  const palette = {
    1: { bg: T.gold,    label: "1ST" },
    2: { bg: T.silver,  label: "2ND" },
    3: { bg: T.bronze,  label: "3RD" },
    4: { bg: "rgba(244,244,246,0.18)", label: "4TH" },
  }[rank];
  if (!palette) return null;
  // When live scoring is active, override the medal color with green/red
  // based on whether the user's pick matches the actual standing for this slot.
  // Pre-tournament `correctness` is null and we render the standard medal.
  const liveOverride = correctness === "correct"
    ? { bg: "rgba(34,197,94,0.95)", text: "#062315" }
    : correctness === "wrong"
    ? { bg: "rgba(248,113,113,0.85)", text: "#3b0a0a" }
    : null;
  const bg = liveOverride ? liveOverride.bg
           : rank <= 3 ? palette.bg
           : "transparent";
  const color = liveOverride ? liveOverride.text
              : rank <= 3 ? "#0A0A0F"
              : T.inkSec;
  return (
    <div style={{
      display:"inline-flex", alignItems:"center", justifyContent:"center",
      minWidth:38, height:24, padding:"0 8px",
      background: bg,
      border: (!liveOverride && rank === 4) ? `1px solid ${T.borderHi}` : "none",
      borderRadius:6,
      fontSize:10, fontWeight:800,
      letterSpacing:"0.06em",
      color,
      fontFamily:FM,
    }}>
      {palette.label}
    </div>
  );
}

// ── Single team row inside a group card ────────────────────────────────
function TeamRow({ teamCode, rank, onTap, disabled, correctness }) {
  const t = TEAMS[teamCode] || { name: teamCode, abbr: teamCode, flag: "" };
  const isRanked = rank != null;
  // Live-scoring tint takes precedence over the standard "ranked" teal:
  //   - "correct" → green tint (user nailed this slot — earning 5 pts)
  //   - "wrong"   → muted red tint (user picked this team but standings disagree)
  //   - null      → no live data, fall through to default styling
  const isCorrect = correctness === "correct";
  const isWrong   = correctness === "wrong";
  const bg = isCorrect ? "rgba(34,197,94,0.10)"
           : isWrong   ? "rgba(248,113,113,0.07)"
           : isRanked  ? T.tealDim
           : "rgba(255,255,255,0.025)";
  const border = isCorrect ? "rgba(34,197,94,0.45)"
              : isWrong   ? "rgba(248,113,113,0.30)"
              : isRanked  ? T.borderTeal
              : T.border;
  return (
    <button
      onClick={onTap}
      disabled={disabled}
      className="no-press"
      style={{
        display:"flex", alignItems:"center", gap:12,
        width:"100%", padding:"11px 12px",
        marginBottom:6,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius:11,
        cursor: disabled ? "default" : "pointer",
        textAlign:"left",
        transition:"background 0.15s, border-color 0.15s, transform 0.1s",
      }}
      onTouchStart={(e) => { if (!disabled) e.currentTarget.style.transform="scale(0.985)"; }}
      onTouchEnd={(e)   => { e.currentTarget.style.transform="scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform="scale(1)"; }}
    >
      <TeamFlag team={t} size={22} style={{ marginRight: 0, flexShrink: 0 }} />
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:14, fontWeight:700, color:T.ink,
          letterSpacing:"-0.01em",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>{t.name}</div>
        <div style={{
          fontSize:10, fontWeight:600, color:T.inkMuted,
          letterSpacing:"0.10em", marginTop:1,
        }}>{t.abbr}</div>
      </div>
      {isRanked
        ? <RankBadge rank={rank} correctness={correctness} />
        : <div style={{
            fontSize:10, fontWeight:700, color:T.inkFaint,
            letterSpacing:"0.10em", textTransform:"uppercase",
          }}>Tap to rank</div>
      }
    </button>
  );
}

// ── A single group card with tap-to-rank logic ──────────────────────────
function GroupCard({ groupLetter, ranking, onUpdate, liveScore, locked = false }) {
  const teams = GROUPS[groupLetter] || [];
  const ranked = Array.isArray(ranking) ? ranking : [];
  const complete = ranked.length === 4;

  const rankFor = (code) => {
    const idx = ranked.indexOf(code);
    return idx === -1 ? null : idx + 1;
  };

  const handleTap = (code) => {
    if (locked) return;                 // post-kickoff: no mutations
    if (complete) return;               // locked once all 4 placed
    if (ranked.includes(code)) return;  // tap-once semantics; Reset to redo
    tap();
    onUpdate([...ranked, code]);
  };

  const handleReset = () => {
    if (locked) return;
    if (ranked.length === 0) return;
    tap();
    onUpdate([]);
  };

  // Live scoring (Layer 3): when liveScore is present AND user has a
  // prediction for this group AND at least one match has been played for
  // these teams, we surface per-row correctness highlights and a header
  // pill. The slotResults array tells us, per slot 0..3, whether the
  // user's predicted team matches the actual standing.
  const showLiveScore = !!(liveScore && liveScore.hasPrediction && liveScore.slotResults?.length === 4);
  // Per-team correctness map: code → "correct" | "wrong" | null. "correct"
  // means user's predicted team is in the position they predicted.
  // "wrong" means the team IS picked by the user but isn't in that slot
  // in actual standings. Both states get color treatment in TeamRow.
  const correctnessByCode = useMemo(() => {
    if (!showLiveScore) return {};
    const out = {};
    for (const r of liveScore.slotResults) {
      if (r.predictedCode) {
        out[r.predictedCode] = r.isCorrect ? "correct" : "wrong";
      }
    }
    return out;
  }, [showLiveScore, liveScore]);

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${complete ? T.borderTeal : T.border}`,
      borderRadius:14,
      padding:"14px 14px 10px",
      marginBottom:12,
      transition:"border-color 0.2s",
    }}>
      {/* Group header */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:10,
      }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
          <span style={{
            fontSize:10, fontWeight:800, color:T.gold,
            letterSpacing:"0.16em", textTransform:"uppercase",
          }}>Group</span>
          <span style={{
            fontFamily:FD, fontSize:22, fontWeight:700, color:T.ink,
            letterSpacing:"-0.02em", lineHeight:1,
          }}>{groupLetter}</span>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          {/* Live-score pill — overrides the standard "Locked in" / "X/4"
              indicator when there's actual scoring data to show. */}
          {showLiveScore ? (
            <span style={{
              fontSize:10, fontWeight:800, color:T.teal,
              letterSpacing:"0.10em", textTransform:"uppercase",
              display:"inline-flex", alignItems:"center", gap:5,
              padding:"3px 8px", borderRadius:999,
              background:"rgba(20,184,166,0.10)",
              border:`1px solid ${T.borderTeal}`,
            }}>
              {liveScore.isComplete ? "Final" : "Live"}
              <span style={{ color:T.ink, fontWeight:800 }}>
                {liveScore.correctSlots}/4 · {liveScore.points}p
              </span>
            </span>
          ) : complete ? (
            <span style={{
              fontSize:10, fontWeight:700, color:T.teal,
              letterSpacing:"0.10em", textTransform:"uppercase",
              display:"inline-flex", alignItems:"center", gap:5,
            }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Locked in
            </span>
          ) : (
            <span style={{
              fontFamily:FM, fontSize:11, fontWeight:700, color:T.inkSec,
            }}>{ranked.length}/4</span>
          )}
          {ranked.length > 0 && !showLiveScore && (
            <button
              onClick={handleReset}
              style={{
                fontSize:10, fontWeight:700, color:T.inkSec,
                letterSpacing:"0.06em", textTransform:"uppercase",
                background:"none", border:"none", cursor:"pointer",
                padding:"4px 6px", marginLeft:4,
              }}
            >Reset</button>
          )}
        </div>
      </div>

      {/* Teams */}
      {teams.map((code) => (
        <TeamRow
          key={code}
          teamCode={code}
          rank={rankFor(code)}
          onTap={() => handleTap(code)}
          disabled={locked || (complete && rankFor(code) == null)}
          correctness={correctnessByCode[code] || null}
        />
      ))}
    </div>
  );
}

// ── Tournament phase timeline ───────────────────────────────────────────
// Visual scrubber that shows the 5 stages of the tournament. Highlights the
// current stage based on today's date. Helps users SEE the journey: where
// they are now → where the bracket leads.
const TOURNAMENT_PHASES = [
  { key: "pre",    label: "Pre",    sub: "Lock bracket",  startMs: null },
  { key: "groups", label: "Groups", sub: "Jun 11–27",    startMs: Date.parse("2026-06-11T00:00:00Z") },
  { key: "r16",    label: "R16",    sub: "Jun 28–Jul 3", startMs: Date.parse("2026-06-28T00:00:00Z") },
  { key: "qf",     label: "QF",     sub: "Jul 3–4",      startMs: Date.parse("2026-07-03T00:00:00Z") },
  { key: "sf",     label: "SF",     sub: "Jul 9–14",     startMs: Date.parse("2026-07-09T00:00:00Z") },
  { key: "final",  label: "Final",  sub: "Jul 19",       startMs: Date.parse("2026-07-19T00:00:00Z") },
];

function getCurrentPhaseIndex(now = new Date()) {
  const t = now.getTime();
  // Walk from the end backwards — the first phase whose start is in the past
  // is the current one. Pre-tournament (idx 0) is the catch-all.
  for (let i = TOURNAMENT_PHASES.length - 1; i >= 1; i--) {
    if (t >= TOURNAMENT_PHASES[i].startMs) return i;
  }
  return 0;
}

function PhaseTimeline({ onJumpToPhase }) {
  const idx = getCurrentPhaseIndex();
  return (
    <div style={{
      padding:"14px 4px 6px",
      marginBottom:6,
    }}>
      <div style={{
        fontSize:9.5, fontWeight:800, color:T.inkMuted,
        letterSpacing:"0.16em", textTransform:"uppercase",
        marginBottom:10, paddingLeft:2,
      }}>Tournament Path</div>
      <div style={{
        position:"relative",
        display:"flex", justifyContent:"space-between", alignItems:"flex-start",
        padding:"0 4px",
      }}>
        {/* Connecting line — runs behind the dots */}
        <div style={{
          position:"absolute", left:"6%", right:"6%", top:7, height:2,
          background:`linear-gradient(90deg, ${T.teal} 0%, ${T.teal} ${(idx / (TOURNAMENT_PHASES.length - 1)) * 100}%, ${T.borderHi} ${(idx / (TOURNAMENT_PHASES.length - 1)) * 100}%, ${T.borderHi} 100%)`,
          borderRadius:1,
        }}/>
        {TOURNAMENT_PHASES.map((p, i) => {
          const isPast    = i < idx;
          const isCurrent = i === idx;
          const tappable  = !!onJumpToPhase;
          // Each phase is a tappable button. We use <button> for a11y rather
          // than a div+onClick, so VoiceOver/screen readers announce the
          // jump as a control. Pre-tournament (idx 0) all phases are
          // tappable; tapping a future phase still jumps to its section
          // even though that section may be locked — the user lands on
          // the locked-state placeholder which is the right answer.
          return (
            <button
              key={p.key}
              onClick={tappable ? () => {
                try { window.navigator.vibrate?.(6); } catch {}
                onJumpToPhase(p.key);
              } : undefined}
              disabled={!tappable}
              className="no-press"
              style={{
                position:"relative", zIndex:1,
                flex:1, minWidth:0,
                display:"flex", flexDirection:"column", alignItems:"center",
                background:"none", border:"none",
                padding:"4px 2px",
                cursor: tappable ? "pointer" : "default",
                // Slight lift on hover/active to signal interactivity
                transition:"transform 0.1s ease",
              }}
              onTouchStart={(e) => { if (tappable) e.currentTarget.style.transform = "translateY(-1px)"; }}
              onTouchEnd={(e)   => { e.currentTarget.style.transform = "translateY(0)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              <div style={{
                width:isCurrent ? 16 : 12, height:isCurrent ? 16 : 12,
                borderRadius:"50%",
                background: isPast || isCurrent ? T.teal : T.bg,
                border: `2px solid ${isPast || isCurrent ? T.teal : T.borderHi}`,
                marginBottom:6,
                marginTop: isCurrent ? -2 : 0,
                boxShadow: isCurrent ? `0 0 0 3px rgba(20,184,166,0.18)` : "none",
                transition:"all 0.3s ease",
              }}/>
              <div style={{
                fontSize:10, fontWeight: isCurrent ? 800 : 700,
                color: isCurrent ? T.teal : isPast ? T.inkSec : T.inkMuted,
                letterSpacing:"0.04em",
                lineHeight:1.1, textAlign:"center",
              }}>{p.label}</div>
              <div style={{
                fontSize:8.5, fontWeight:600,
                color: isCurrent ? T.inkSec : T.inkMuted,
                marginTop:2, lineHeight:1, textAlign:"center",
                letterSpacing:"0.02em",
                fontFamily:FM,
              }}>{p.sub}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── How This Works panel ────────────────────────────────────────────────
// Collapsible explainer. Default-open the first time the user sees the
// bracket; after they dismiss it once, default-closed (remembered via
// localStorage). They can always reopen it.
const HOW_IT_WORKS_KEY = "torch_wc2026_how_dismissed";

function HowItWorksPanel() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(HOW_IT_WORKS_KEY) !== "1"; }
    catch { return true; }
  });
  const handleClose = () => {
    try { localStorage.setItem(HOW_IT_WORKS_KEY, "1"); } catch {}
    setOpen(false);
  };
  const handleOpen = () => {
    try { localStorage.removeItem(HOW_IT_WORKS_KEY); } catch {}
    setOpen(true);
  };

  if (!open) {
    return (
      <button
        onClick={handleOpen}
        style={{
          width:"100%", marginBottom:14,
          padding:"10px 14px",
          background:"transparent",
          border:`1px solid ${T.border}`,
          borderRadius:11,
          color:T.inkSec, fontSize:12, fontWeight:600,
          cursor:"pointer", textAlign:"left",
          display:"flex", alignItems:"center", justifyContent:"space-between",
        }}
      >
        <span>How does this work?</span>
        <span style={{ color:T.inkMuted, fontSize:14 }}>+</span>
      </button>
    );
  }

  return (
    <div style={{
      marginBottom:16, padding:"14px 14px 4px",
      background:"rgba(20,184,166,0.04)",
      border:`1px solid ${T.borderTeal}`,
      borderRadius:13,
    }}>
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:12,
      }}>
        <div style={{
          fontSize:9.5, fontWeight:800, color:T.teal,
          letterSpacing:"0.16em", textTransform:"uppercase",
        }}>How It Works</div>
        <button
          aria-label="Hide"
          onClick={handleClose}
          style={{
            background:"none", border:"none", color:T.inkMuted,
            fontSize:18, lineHeight:1, padding:4, cursor:"pointer",
          }}
        >×</button>
      </div>

      {/* Three short sections — accurate model:
            1. Score every round (covers full point ladder)
            2. Group stage locks June 11 (only locks the group picks at kickoff)
            3. Knockouts unlock progressively (one round at a time, picks lock per match)
            4. Highest score wins (clarifies 1000 = perfect bracket score, separate
               from any non-bracket point sources) */}
      {[
        {
          icon: "🎯",
          title: "Score every round",
          body: `Group stage: ${SCORING.GROUP_STAGE_PER_SLOT} pts per correct placement (1st–4th in each of 12 groups). Knockouts escalate: ${SCORING.R32_WINNER} → ${SCORING.R16_WINNER} → ${SCORING.QF_WINNER} → ${SCORING.SF_WINNER} pts per correct winner. Pick the champion: ${SCORING.CHAMPION} pts.`,
        },
        {
          icon: "📅",
          title: "Group picks lock June 11",
          body: "Lock your 12 group standings, Golden Boot pick, and tiebreaker before kickoff. After the opener, group picks are final — no edits.",
        },
        {
          icon: "🔓",
          title: "Knockouts unlock as rounds finish",
          body: "Round of 32 opens June 28 with the real matchups. Each later round (R16 → QF → SF → Final) opens as the previous round wraps. Pick winners against actual matchups, not predicted ones.",
        },
        {
          icon: "👑",
          title: "Highest bracket score wins",
          body: `Leaderboard updates after every match. ${SCORING.TOTAL_MAX.toLocaleString()} pts = a perfect bracket. Tiebreaker: total goals scored in the final. Whoever leads on July 19 takes the league.`,
        },
      ].map((s, i) => (
        <div key={i} style={{
          display:"flex", gap:11, marginBottom:11, alignItems:"flex-start",
        }}>
          <div style={{
            flexShrink:0, fontSize:18, lineHeight:1.1, marginTop:-1,
          }}>{s.icon}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontSize:13, fontWeight:700, color:T.ink,
              letterSpacing:"-0.005em", lineHeight:1.3,
              marginBottom:3,
            }}>{s.title}</div>
            <div style={{
              fontSize:11.5, color:T.inkSec, lineHeight:1.5,
            }}>{s.body}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Locked-in hero ──────────────────────────────────────────────────────
// Replaces the standard "Group Stage" header when all 12 groups are locked.
// Celebrates the milestone + explains what comes next (since most users will
// open the app well before kickoff and wonder "now what?").
function LockedInHero({ predictions, onJumpToKnockouts, onShareBracket }) {
  // Now triggers when EVERYTHING currently lockable is locked. Phase 1:
  // pre-tournament that's groups + Golden Boot + Tiebreaker (knockouts gated
  // until June 28). During tournament: also includes unlocked-round picks.
  // overallProgress() reads phase-aware so the threshold moves automatically.
  const overall = overallProgress(predictions);
  if (overall.pct < 1) return null;

  const phaseIdx = getCurrentPhaseIndex();
  const preTournament = phaseIdx === 0;

  // Champion only exists if the FINAL round is unlocked AND the user has
  // picked it. Pre-tournament FINAL is locked, so champion will always be
  // null and the hero falls back to a generic pre-tournament celebration.
  // Phase 2B: predictions.knockouts.FINAL is an object keyed by matchSlot,
  // not an array — the champion lives at the "FINAL-1" key.
  const storedFinal = predictions?.knockouts?.FINAL?.["FINAL-1"];
  const championCode = isValidTeamCode(storedFinal) ? storedFinal : null;
  const champion = championCode ? TEAMS[championCode] : null;

  return (
    <div style={{
      position:"relative", overflow:"hidden",
      marginBottom:16,
      padding:"18px 16px",
      background:"rgba(20,184,166,0.06)",
      border:`1px solid ${T.borderTeal}`,
      borderRadius:14,
    }}>

      <div style={{ position:"relative", zIndex:1 }}>
        {/* Status pill */}
        <div style={{
          display:"inline-flex", alignItems:"center", gap:6,
          padding:"4px 10px", marginBottom:10,
          background:T.tealDim, border:`1px solid ${T.borderTeal}`,
          borderRadius:999,
          fontSize:10, fontWeight:800, color:T.teal,
          letterSpacing:"0.12em", textTransform:"uppercase",
        }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {preTournament ? "Pre-Tournament Locked In" : "Bracket Locked In"}
        </div>

        {/* Hero copy */}
        <div style={{
          fontFamily:FD, fontSize:20, fontWeight:700, color:T.ink,
          letterSpacing:"-0.02em", lineHeight:1.15,
          marginBottom:6,
        }}>
          {preTournament
            ? "All set for kickoff. See you June 11."
            : "Bracket complete. You're locked in."}
        </div>
        <div style={{
          fontSize:12.5, color:T.inkSec, lineHeight:1.5,
          marginBottom:14,
        }}>
          {preTournament
            ? "Your group placements, Golden Boot pick, and tiebreaker are set. Knockout rounds open as the real matchups are decided — Round of 32 unlocks June 28."
            : "Your picks are final. Watch the leaderboard for live scoring updates."}
          {champion && (
            <> Your champion: <span style={{ color:T.ink, fontWeight:700 }}><TeamFlag team={champion} size={14} />{champion.name}</span>.</>
          )}
        </div>

        {/* What happens next — only meaningful pre-tournament. The journey
            now explicitly calls out that knockouts open progressively, not
            all at once — matches the new reality that real matchups are
            only known as the tournament unfolds. */}
        {preTournament && (
          <div style={{
            padding:"11px 12px", marginBottom:0,
            background:"rgba(0,0,0,0.20)",
            border:`1px solid ${T.border}`,
            borderRadius:11,
          }}>
            <div style={{
              fontSize:9.5, fontWeight:800, color:T.gold,
              letterSpacing:"0.14em", textTransform:"uppercase",
              marginBottom:8,
            }}>What Happens Next</div>
            {[
              ["📅", "June 11", "Tournament kicks off · group picks lock at first whistle"],
              ["⚽", "June 27", "Group stage ends · 32 teams advance to knockouts"],
              ["🔓", "June 28", "Round of 32 unlocks · pick winners of real matchups"],
              ["🔥", "Jul 4 onward", "R16 → QF → SF unlock as each round finishes"],
              ["🏆", "July 19", "The Final · highest score wins your league"],
            ].map(([icon, when, what]) => (
              <div key={when} style={{ display:"flex", gap:9, marginBottom:6, alignItems:"flex-start" }}>
                <div style={{ flexShrink:0, fontSize:13, marginTop:1 }}>{icon}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <span style={{ fontSize:11, fontWeight:800, color:T.ink, fontFamily:FM, letterSpacing:"0.02em" }}>{when}</span>
                  <span style={{ fontSize:11, color:T.inkSec, marginLeft:6 }}>{what}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Share CTA — only renders when parent passes onShareBracket. Sits
            below the "What Happens Next" timeline so the celebration reads
            top-down: status → champion → journey → share. */}
        {onShareBracket && (
          <button
            onClick={() => { try { window.navigator.vibrate?.(8); } catch {} onShareBracket(); }}
            style={{
              width:"100%", marginTop:14,
              padding:"12px 14px",
              background:T.teal,
              border:"none", borderRadius:12,
              color:"#fff", fontSize:13, fontWeight:700,
              letterSpacing:"-0.005em", cursor:"pointer",
              boxShadow:"0 4px 14px rgba(20,184,166,0.30)",
              display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            Share My Bracket
          </button>
        )}
      </div>
    </div>
  );
}

// ── Bracket Sharing — Canvas → PNG export ──────────────────────────────
// Generates a 1080x1350 (4:5 Instagram-friendly) PNG of the user's bracket
// highlights. Uses the Canvas API directly (no html2canvas dependency) to
// keep the bundle lean and avoid known iOS WKWebView issues with foreignObject.
//
// Apple Color Emoji renders cleanly in canvas on iOS, which is most users.
// On Android/desktop the emoji glyphs may render flat — acceptable degradation
// since the share-out destination (iMessage, WhatsApp, etc.) will still
// display the resulting PNG correctly.

const SHARE_CARD_W = 1080;
const SHARE_CARD_H = 1350;

// Wraps text to fit within maxWidth, returns array of lines.
function wrapCanvasText(ctx, text, maxWidth) {
  const words = String(text || "").split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? line + " " + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
}

async function renderBracketCardPNG({ predictions, username, leagueName }) {
  // Phase 1: Pre-tournament card. No champion section since the FINAL round
  // is locked until July 19. Card focuses on what's actually been picked:
  // 12 group placements + Golden Boot + Tiebreaker. Phase 2 will add live
  // champion + runner-up rendering once the FINAL round unlocks.
  // Phase 2B: predictions.knockouts is keyed by matchSlot strings.
  const storedFinal = predictions?.knockouts?.FINAL?.["FINAL-1"];
  const championCode = isValidTeamCode(storedFinal) ? storedFinal : null;
  const champion = championCode ? TEAMS[championCode] : null;
  // Runner-up — same source: stored SF picks. The team in the user's SF picks
  // that ISN'T the champion. Pre-tournament both will be null. SF is stored as
  // { "SF-1": "...", "SF-2": "..." }; Object.values works for both object and
  // legacy array shapes.
  const sfRaw = predictions?.knockouts?.SF;
  const sfPicks = sfRaw && typeof sfRaw === "object" ? Object.values(sfRaw) : [];
  const runnerUpCode = championCode && sfPicks.length === 2
    ? sfPicks.find((c) => isValidTeamCode(c) && c !== championCode) || null
    : null;
  const runnerUp = runnerUpCode ? TEAMS[runnerUpCode] : null;

  // Group-stage stats for the pre-tournament card hero. Counts how many
  // groups the user fully placed (1st-4th).
  const groupsObj = predictions?.groups || {};
  const groupsLocked = GROUP_LETTERS.filter(L =>
    Array.isArray(groupsObj[L]) && groupsObj[L].length === 4
  ).length;

  const goldenBoot = predictions?.goldenBoot || "—";
  const totalGoals = predictions?.totalGoals || "—";

  const canvas = document.createElement("canvas");
  canvas.width = SHARE_CARD_W;
  canvas.height = SHARE_CARD_H;
  const ctx = canvas.getContext("2d");

  // ── Background ──
  const bg = ctx.createLinearGradient(0, 0, 0, SHARE_CARD_H);
  bg.addColorStop(0, "#0E0E12");
  bg.addColorStop(1, "#08080A");
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SHARE_CARD_W, SHARE_CARD_H);

  // Subtle teal glow top-right
  const glow1 = ctx.createRadialGradient(SHARE_CARD_W * 0.85, 100, 0, SHARE_CARD_W * 0.85, 100, 500);
  glow1.addColorStop(0, "rgba(20,184,166,0.18)");
  glow1.addColorStop(1, "rgba(20,184,166,0)");
  ctx.fillStyle = glow1;
  ctx.fillRect(0, 0, SHARE_CARD_W, SHARE_CARD_H);

  // Gold accent bottom-left
  const glow2 = ctx.createRadialGradient(120, SHARE_CARD_H - 200, 0, 120, SHARE_CARD_H - 200, 400);
  glow2.addColorStop(0, "rgba(229,180,89,0.10)");
  glow2.addColorStop(1, "rgba(229,180,89,0)");
  ctx.fillStyle = glow2;
  ctx.fillRect(0, 0, SHARE_CARD_W, SHARE_CARD_H);

  const FAMILY = `'Helvetica Neue', 'Apple Color Emoji', system-ui, -apple-system, sans-serif`;

  // ── Top header strip — SPECIAL EVENT pill ──
  ctx.fillStyle = "rgba(20,184,166,0.12)";
  roundRect(ctx, 60, 70, 200, 38, 999);
  ctx.fill();
  ctx.strokeStyle = "rgba(20,184,166,0.40)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, 60, 70, 200, 38, 999);
  ctx.stroke();

  ctx.font = `800 16px ${FAMILY}`;
  ctx.fillStyle = "#14B8A6";
  ctx.textBaseline = "middle";
  ctx.fillText("★ SPECIAL EVENT", 78, 89);

  // ── World Cup 2026 header ──
  ctx.font = `800 20px ${FAMILY}`;
  ctx.fillStyle = "#E5B459";
  ctx.fillText("🏆  WORLD CUP 2026", 60, 150);

  // ── My Bracket eyebrow ──
  ctx.font = `800 14px ${FAMILY}`;
  ctx.fillStyle = "rgba(244,244,246,0.45)";
  ctx.fillText("MY BRACKET", 60, 195);

  // ── Username ──
  ctx.font = `800 36px ${FAMILY}`;
  ctx.fillStyle = "#F4F4F6";
  const displayName = (username || "Anonymous") + "'s prediction";
  const wrappedName = wrapCanvasText(ctx, displayName, SHARE_CARD_W - 120);
  let nameY = 240;
  wrappedName.slice(0, 2).forEach((line) => {
    ctx.fillText(line, 60, nameY);
    nameY += 44;
  });

  // ── Hero section ──
  // With champion: huge flag + country + "TO WIN THE CUP" (during/post-tournament)
  // Without champion: trophy emoji + "GROUPS LOCKED IN" + "X/12 GROUPS" stat
  //   (pre-tournament — what the user has actually committed to so far)
  const heroY = 380;
  const heroH = 380;
  // Subtle backing card for the hero
  ctx.fillStyle = "rgba(229,180,89,0.05)";
  roundRect(ctx, 60, heroY, SHARE_CARD_W - 120, heroH, 24);
  ctx.fill();
  ctx.strokeStyle = "rgba(229,180,89,0.30)";
  ctx.lineWidth = 1.5;
  roundRect(ctx, 60, heroY, SHARE_CARD_W - 120, heroH, 24);
  ctx.stroke();

  if (champion) {
    // Big flag
    ctx.font = `200px ${FAMILY}`;
    ctx.textAlign = "center";
    ctx.fillStyle = "#fff";
    ctx.fillText(champion.flag, SHARE_CARD_W / 2, heroY + 200);

    // Champion name
    ctx.font = `800 56px ${FAMILY}`;
    ctx.fillStyle = "#F4F4F6";
    const nameLines = wrapCanvasText(ctx, champion.name.toUpperCase(), SHARE_CARD_W - 200);
    let cy = heroY + 270;
    nameLines.forEach((line) => {
      ctx.fillText(line, SHARE_CARD_W / 2, cy);
      cy += 60;
    });

    // To Win The Cup
    ctx.font = `700 18px ${FAMILY}`;
    ctx.fillStyle = "#E5B459";
    ctx.fillText("TO WIN THE CUP", SHARE_CARD_W / 2, heroY + heroH - 30);
  } else {
    // Pre-tournament — celebrate the early commit
    ctx.textAlign = "center";

    // Trophy
    ctx.font = `200px ${FAMILY}`;
    ctx.fillStyle = "#fff";
    ctx.fillText("🏆", SHARE_CARD_W / 2, heroY + 200);

    // "GROUPS LOCKED IN"
    ctx.font = `800 48px ${FAMILY}`;
    ctx.fillStyle = "#F4F4F6";
    ctx.fillText("GROUPS LOCKED IN", SHARE_CARD_W / 2, heroY + 280);

    // Stat
    ctx.font = `700 24px ${FAMILY}`;
    ctx.fillStyle = "#E5B459";
    ctx.fillText(`${groupsLocked}/12 groups · all 48 teams placed`, SHARE_CARD_W / 2, heroY + 320);

    // Tease what's next
    ctx.font = `600 16px ${FAMILY}`;
    ctx.fillStyle = "rgba(244,244,246,0.55)";
    ctx.fillText("Knockouts unlock June 28", SHARE_CARD_W / 2, heroY + heroH - 30);
  }
  ctx.textAlign = "left"; // reset

  // ── Final matchup row — only shown if user has actually picked a final.
  //    Pre-tournament FINAL is locked, so this whole row is skipped and the
  //    Golden Boot / Tiebreaker rows shift up to fill the space. ──
  const showFinalRow = !!(champion && runnerUp);
  const bootStartY = showFinalRow ? 910 : 800;

  if (showFinalRow) {
    const finalY = 800;
    ctx.fillStyle = "rgba(255,255,255,0.025)";
    roundRect(ctx, 60, finalY, SHARE_CARD_W - 120, 90, 16);
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.06)";
    ctx.lineWidth = 1;
    roundRect(ctx, 60, finalY, SHARE_CARD_W - 120, 90, 16);
    ctx.stroke();

    ctx.font = `800 13px ${FAMILY}`;
    ctx.fillStyle = "rgba(244,244,246,0.45)";
    ctx.fillText("THE FINAL", 90, finalY + 26);

    ctx.font = `700 28px ${FAMILY}`;
    ctx.fillStyle = "#F4F4F6";
    ctx.fillText(`${champion.flag} ${champion.name}  vs  ${runnerUp.flag} ${runnerUp.name}`, 90, finalY + 65);
  }

  // ── Golden Boot row ──
  const bootY = bootStartY;
  ctx.fillStyle = "rgba(255,255,255,0.025)";
  roundRect(ctx, 60, bootY, SHARE_CARD_W - 120, 90, 16);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  roundRect(ctx, 60, bootY, SHARE_CARD_W - 120, 90, 16);
  ctx.stroke();

  ctx.font = `800 13px ${FAMILY}`;
  ctx.fillStyle = "rgba(244,244,246,0.45)";
  ctx.fillText("⚽  GOLDEN BOOT", 90, bootY + 26);

  ctx.font = `700 28px ${FAMILY}`;
  ctx.fillStyle = "#F4F4F6";
  ctx.fillText(goldenBoot, 90, bootY + 65);

  // ── Tiebreaker row ──
  const tieY = bootY + 110;
  ctx.fillStyle = "rgba(255,255,255,0.025)";
  roundRect(ctx, 60, tieY, SHARE_CARD_W - 120, 90, 16);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  roundRect(ctx, 60, tieY, SHARE_CARD_W - 120, 90, 16);
  ctx.stroke();

  ctx.font = `800 13px ${FAMILY}`;
  ctx.fillStyle = "rgba(244,244,246,0.45)";
  ctx.fillText("🎯  GOALS IN THE FINAL", 90, tieY + 26);

  ctx.font = `700 28px ${FAMILY}`;
  ctx.fillStyle = "#F4F4F6";
  ctx.fillText(String(totalGoals), 90, tieY + 65);

  // ── Footer ──
  const footerY = SHARE_CARD_H - 100;
  // Hairline above footer
  ctx.strokeStyle = "rgba(255,255,255,0.06)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, footerY - 30);
  ctx.lineTo(SHARE_CARD_W - 60, footerY - 30);
  ctx.stroke();

  // Torch flame icon (drawn as text emoji to keep it self-contained)
  ctx.font = `28px ${FAMILY}`;
  ctx.fillStyle = "#FF6B3D";
  ctx.fillText("🔥", 60, footerY + 20);

  // "torch" wordmark
  ctx.font = `800 26px ${FAMILY}`;
  ctx.fillStyle = "#F4F4F6";
  ctx.fillText("torch", 105, footerY + 20);

  // Tagline
  ctx.font = `500 16px ${FAMILY}`;
  ctx.fillStyle = "rgba(244,244,246,0.62)";
  ctx.fillText("Make your picks  ·  torchpicks.com", 60, footerY + 55);

  // Done. Convert to PNG blob.
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => blob ? resolve(blob) : reject(new Error("Canvas toBlob returned null")),
      "image/png",
      0.95
    );
  });
}

// Helper: rounded-rectangle path (Canvas API doesn't have one built in until
// browser support for ctx.roundRect catches up; rolling our own keeps it
// reliable across iOS Safari versions).
function roundRect(ctx, x, y, w, h, r) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
}

// ── Share Bracket Modal ─────────────────────────────────────────────────
// Triggered from the LockedInHero. Shows a preview of the share card,
// then offers Share / Save / Copy actions. Three share paths:
//   1. navigator.share() with PNG file → iOS native share sheet (best UX)
//   2. Download the PNG → user manually attaches it wherever
//   3. Copy text-only link → fallback for desktop or older browsers
function ShareBracketModal({ open, onClose, predictions, username, leagueName }) {
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  // Generate the preview the moment the modal opens. Cached in state so the
  // user can stare at it while deciding what to do; revoked on close.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    let urlToRevoke = null;
    setPreviewError(null);
    setPreviewUrl(null);
    (async () => {
      try {
        const blob = await renderBracketCardPNG({ predictions, username, leagueName });
        if (!alive) return;
        const url = URL.createObjectURL(blob);
        urlToRevoke = url;
        setPreviewUrl(url);
      } catch (e) {
        console.error("[Share] render failed:", e);
        if (alive) setPreviewError("Couldn't render the card. Take a screenshot instead!");
      }
    })();
    return () => {
      alive = false;
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [open, predictions, username, leagueName]);

  if (!open) return null;

  const handleShareImage = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await renderBracketCardPNG({ predictions, username, leagueName });
      const file = new File([blob], "my-world-cup-2026-bracket.png", { type: "image/png" });
      // navigator.share with files works on iOS Safari 15+ and most modern mobile.
      // Capacitor WKWebView supports it. Fall back to download otherwise.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My World Cup 2026 Bracket",
          text: "My World Cup 2026 picks 🏆  Make yours: torchpicks.com",
        });
        try { window.navigator.vibrate?.(10); } catch {}
      } else {
        // Fallback path: trigger a download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "my-world-cup-2026-bracket.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        window.torchToast?.("Image downloaded — share it from your camera roll", "success");
      }
    } catch (e) {
      // User cancelled the share sheet (AbortError) — silent. Real failures get logged + toasted.
      if (e?.name !== "AbortError") {
        console.error("[Share] failed:", e);
        window.torchToast?.("Couldn't share — try the screenshot button", "error");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCopyLink = async () => {
    const text = "Check out my World Cup 2026 bracket on Torch! 🏆\n\nMake yours: torchpicks.com";
    try {
      if (navigator.share) {
        await navigator.share({ title: "My WC2026 Bracket", text });
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        window.torchToast?.("Link copied to clipboard", "success");
      } else {
        window.torchToast?.("Sharing not supported on this device", "error");
      }
      try { window.navigator.vibrate?.(8); } catch {}
    } catch (e) {
      if (e?.name !== "AbortError") {
        console.error("[Share] link failed:", e);
      }
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position:"fixed", inset:0, zIndex:10000,
        background:"rgba(0,0,0,0.78)",
        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"flex-end",
        padding:"40px 16px calc(20px + env(safe-area-inset-bottom, 0px))",
        WebkitBackdropFilter:"blur(8px)", backdropFilter:"blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:"100%", maxWidth:480,
          display:"flex", flexDirection:"column", alignItems:"stretch",
          maxHeight:"100%",
        }}
      >
        {/* Preview image — long-press to save on iOS */}
        <div style={{
          flex:1, minHeight:0,
          display:"flex", alignItems:"center", justifyContent:"center",
          marginBottom:18,
          overflow:"hidden",
        }}>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt="Your bracket"
              style={{
                maxWidth:"100%", maxHeight:"100%",
                width:"auto", height:"auto",
                borderRadius:14,
                boxShadow:"0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.06)",
              }}
            />
          ) : previewError ? (
            <div style={{
              padding:"40px 16px", textAlign:"center",
              color:T.inkSec, fontSize:13, lineHeight:1.5,
            }}>{previewError}</div>
          ) : (
            <div style={{
              padding:"60px 16px", textAlign:"center",
            }}>
              <div style={{
                width:24, height:24, margin:"0 auto 12px",
                border:`2px solid ${T.border}`,
                borderTopColor:T.teal,
                borderRadius:"50%",
                animation:"wcSpin 0.7s linear infinite",
              }}/>
              <div style={{ color:T.inkMuted, fontSize:12 }}>Rendering your bracket…</div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={{
          display:"flex", flexDirection:"column", gap:8,
        }}>
          <button
            onClick={handleShareImage}
            disabled={busy || !previewUrl}
            style={{
              width:"100%", padding:"14px",
              borderRadius:13, border:"none",
              background: (busy || !previewUrl)
                ? "rgba(20,184,166,0.30)"
                : T.teal,
              color:"#fff", fontSize:14, fontWeight:700,
              letterSpacing:"0.01em",
              cursor: (busy || !previewUrl) ? "not-allowed" : "pointer",
              boxShadow: (busy || !previewUrl) ? "none" : "0 4px 14px rgba(20,184,166,0.30)",
              display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
              <polyline points="16 6 12 2 8 6"/>
              <line x1="12" y1="2" x2="12" y2="15"/>
            </svg>
            {busy ? "Preparing…" : "Share Image"}
          </button>

          <button
            onClick={handleCopyLink}
            style={{
              width:"100%", padding:"12px",
              borderRadius:13,
              background:"rgba(255,255,255,0.04)",
              border:`1px solid ${T.borderHi}`,
              color:T.ink, fontSize:13, fontWeight:600,
              cursor:"pointer",
              display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
            </svg>
            Share Text Link Instead
          </button>

          <div style={{
            fontSize:11, color:T.inkMuted, textAlign:"center",
            padding:"6px 0 0", lineHeight:1.5,
          }}>
            iOS tip: long-press the image to save it to your camera roll
          </div>

          <button
            onClick={onClose}
            style={{
              width:"100%", padding:"10px",
              borderRadius:13,
              background:"transparent", border:"none",
              color:T.inkSec, fontSize:12, fontWeight:600,
              cursor:"pointer", marginTop:4,
            }}
          >Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Bracket Section Tabs ────────────────────────────────────────────────
// Horizontal pill tabs for navigating between the four bracket sections:
// Groups · Knockouts · Golden Boot · Tiebreaker. Each tab shows progress
// (X/Y or ✓), so the user can see at a glance which sections are done.
// Sticky to the top of the body so it's always visible while scrolling.
function BracketSectionTabs({ view, setView, predictions }) {
  const groupProg = groupStageProgress(predictions);
  const koProg    = knockoutProgress(predictions);
  const gbDone    = goldenBootDone(predictions);
  const tbDone    = tiebreakerDone(predictions);
  // Pre-tournament: koProg.total === 0 (no rounds unlocked yet). Show a
  // lock glyph in the tab pill so the user knows it's not a "0/0 done"
  // problem — it's a "wait until June 28" reality.
  const koLocked = koProg.total === 0;

  const tabs = [
    {
      key: "groups",
      label: "Groups",
      progress: `${groupProg.completed}/${groupProg.total}`,
      done: groupProg.pct === 1,
    },
    {
      key: "knockouts",
      label: "Knockouts",
      progress: koLocked ? "🔒" : `${koProg.completed}/${koProg.total}`,
      done: koProg.pct === 1 && koProg.total > 0,
    },
    {
      key: "goldenBoot",
      label: "Boot",
      progress: gbDone ? "✓" : "—",
      done: gbDone,
    },
    {
      key: "tiebreaker",
      label: "Tie",
      progress: tbDone ? "✓" : "—",
      done: tbDone,
    },
  ];

  return (
    <div style={{
      position:"sticky", top:-1, zIndex:2,
      margin:"0 -14px 14px", padding:"6px 14px 8px",
      background:T.bg,
      borderBottom:`1px solid rgba(255,255,255,0.04)`,
      display:"flex", gap:6, overflowX:"auto",
      WebkitOverflowScrolling:"touch",
      scrollbarWidth:"none",
    }}>
      {tabs.map((t) => {
        const active = view === t.key;
        return (
          <button
            key={t.key}
            onClick={() => { tap(); setView(t.key); }}
            style={{
              flexShrink:0,
              display:"inline-flex", alignItems:"center", gap:6,
              padding:"6px 11px",
              borderRadius:999,
              background: active ? T.tealDim : "rgba(255,255,255,0.03)",
              border:`1px solid ${active ? T.borderTeal : T.border}`,
              color: active ? T.teal : t.done ? T.ink : T.inkSec,
              fontSize:11.5, fontWeight:700,
              letterSpacing:"-0.005em",
              cursor:"pointer",
              transition:"background 0.15s, border-color 0.15s, color 0.15s",
            }}
          >
            <span>{t.label}</span>
            <span style={{
              fontFamily:FM, fontSize:10, fontWeight:700,
              color: active ? T.teal : t.done ? T.teal : T.inkMuted,
              opacity: 0.85,
            }}>{t.progress}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Group stage view ────────────────────────────────────────────────────
function GroupStageView({ predictions, onUpdate, locked = false }) {
  const setGroup = (letter, ranking) => {
    if (locked) return;
    onUpdate({
      ...predictions,
      groups: { ...predictions.groups, [letter]: ranking },
    });
  };

  const anyPicks = GROUP_LETTERS.some(L => (predictions?.groups?.[L]?.length || 0) > 0);

  const resetAll = () => {
    if (locked) return;
    if (!anyPicks) return;
    if (!window.confirm("Clear every group pick? You'll start over from scratch.")) return;
    try { window.navigator.vibrate?.(12); } catch {}
    const cleared = {};
    GROUP_LETTERS.forEach(L => { cleared[L] = []; });
    onUpdate({ ...predictions, groups: cleared });
  };

  // ── Live scoring (Layer 3) ─────────────────────────────────────────
  // Subscribe to /worldCup2026Live/standings — populated by the Cloud
  // Function during June 11-27. Pre-tournament returns null and the live
  // scoring UI gracefully hides. Once data flows in, each group card gets
  // a `liveScore` prop and the section header surfaces the running total.
  const liveStandings = useWorldCupLiveStandings();
  const liveData = useMemo(
    () => computeGroupStageScore(predictions, liveStandings),
    [predictions, liveStandings]
  );
  const showLive = hasAnyLiveData(liveStandings);
  const lastUpdated = liveStandings?.lastUpdated;
  const lastUpdatedLabel = formatLastUpdatedAgo(lastUpdated);

  return (
    <div>
      {/* Section intro */}
      <div style={{
        padding:"4px 4px 14px", marginBottom:4,
        display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:12,
      }}>
        <div style={{ flex:1 }}>
          <div style={{
            fontFamily:FD, fontSize:24, fontWeight:700,
            color:T.ink, letterSpacing:"-0.025em",
            marginBottom:6, lineHeight:1.1,
          }}>
            Group Stage
          </div>
          <div style={{
            fontSize:12, color:T.inkSec, lineHeight:1.5,
          }}>
            Tap teams in order — first tap is 1st place, then 2nd, 3rd, 4th. {SCORING.GROUP_STAGE_PER_SLOT} pts for each correctly-placed team.
          </div>
        </div>
        {anyPicks && (
          <button
            onClick={resetAll}
            style={{
              flexShrink:0, alignSelf:"flex-start",
              background:"transparent", border:"none", cursor:"pointer",
              color:T.inkMuted, fontSize:11, fontWeight:600,
              letterSpacing:"0.04em", textTransform:"uppercase",
              padding:"4px 6px", marginTop:2,
            }}
          >Reset All</button>
        )}
      </div>

      {/* Live score header — only when actual matches have been played.
          Pre-tournament this is hidden entirely; we only surface the panel
          when there's something to score against. The number animates in
          smoothly because React re-renders on each new standings snapshot. */}
      {showLive && (
        <div style={{
          marginBottom:14, padding:"14px 14px",
          background:"rgba(20,184,166,0.06)",
          border:`1px solid ${T.borderTeal}`,
          borderRadius:13,
          display:"flex", alignItems:"center", gap:14,
        }}>
          {/* Pulsing live dot */}
          <div style={{
            width:34, height:34, borderRadius:11, flexShrink:0,
            background:"rgba(20,184,166,0.12)",
            border:`1px solid ${T.borderTeal}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            position:"relative",
          }}>
            <span style={{
              width:8, height:8, borderRadius:"50%",
              background:T.teal, display:"block",
              boxShadow:`0 0 0 0 ${T.teal}55`,
              animation:"wcLivePulse 1.6s ease-out infinite",
            }}/>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontSize:9.5, fontWeight:800, color:T.teal,
              letterSpacing:"0.16em", textTransform:"uppercase",
              marginBottom:3,
            }}>
              {liveData.isFullyComplete ? "Group stage final" : "Live · projected"}
            </div>
            <div style={{
              fontFamily:FD, fontSize:18, fontWeight:700,
              color:T.ink, letterSpacing:"-0.01em", lineHeight:1.1,
              marginBottom:2,
            }}>
              {liveData.totalPoints} <span style={{ color:T.inkSec, fontSize:13, fontWeight:600 }}>/ {liveData.totalMax} pts</span>
            </div>
            <div style={{ fontSize:10.5, color:T.inkMuted }}>
              {liveData.matchesPlayed} {liveData.matchesPlayed === 1 ? "match" : "matches"} played{lastUpdatedLabel ? ` · updated ${lastUpdatedLabel}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Inline keyframes for the live-pulse dot */}
      <style>{`
        @keyframes wcLivePulse {
          0%   { box-shadow: 0 0 0 0 ${T.teal}66; }
          70%  { box-shadow: 0 0 0 8px ${T.teal}00; }
          100% { box-shadow: 0 0 0 0 ${T.teal}00; }
        }
      `}</style>

      {/* Slim sticky reminder — stays visible when scrolling through groups */}
      <div style={{
        position:"sticky", top:-1, zIndex:1,
        margin:"0 -14px 12px", padding:"6px 14px",
        background:T.bg,
        borderBottom:`1px solid rgba(255,255,255,0.04)`,
        fontSize:10.5, fontWeight:700, color:T.inkSec,
        letterSpacing:"0.06em", textTransform:"uppercase",
      }}>
        1st → 2nd → 3rd → 4th &nbsp;·&nbsp; Tap to rank
      </div>

      {GROUP_LETTERS.map((L) => (
        <GroupCard
          key={L}
          groupLetter={L}
          ranking={predictions?.groups?.[L]}
          onUpdate={(r) => setGroup(L, r)}
          liveScore={showLive ? liveData.groups[L] : null}
          locked={locked}
        />
      ))}
    </div>
  );
}

// ── Knockouts View (Phase 1 · phase-aware) ─────────────────────────────
// Pre-tournament, every round is locked behind its unlock date. The user
// sees a stack of 5 round cards explaining when each unlocks. Phase 2 will
// rip these placeholders out and render real per-match cards as live API
// data flows in for unlocked rounds.
//
// Why no UI for picking yet: real bracket pools work round-by-round. R32
// matchups are determined by group results (June 27); R16 matchups by R32
// results; etc. There's no honest way to surface a pickable card today —
// we don't know who'll play whom. So the round-card list says "unlocks
// June 28" and we lean into the anticipation.

// ── Knockout Match Team Row ──────────────────────────────────────────────
//
// Single row for one side of a match. Three visual modes:
//   • label only (pre-data) — "Winner Group A" / "3rd of A/B/C/D/F", greyed.
//     `teamCode` is null, `label` is the position string. Disabled.
//   • pickable                — team flag + name + abbr; tap to set pick.
//     `picked` toggles the teal selection treatment.
//   • result                  — same layout plus score, with winner row
//     tinted green and loser row dimmed. `correctness` colors the user's
//     picked row (green=correct, red=wrong) but only applies if the user
//     actually picked this side.
function KnockoutMatchTeamRow({
  teamCode, label, score,
  picked, isWinner, isLoser, correctness,
  disabled, onTap,
}) {
  const t = teamCode ? (TEAMS[teamCode] || { name: teamCode, abbr: teamCode, flag: "" }) : null;

  const isCorrect = correctness === "correct";
  const isWrong   = correctness === "wrong";

  // Background priority: correctness > winner/loser > picked > default
  const bg = isCorrect       ? "rgba(34,197,94,0.10)"
           : isWrong         ? "rgba(248,113,113,0.07)"
           : isWinner        ? "rgba(20,184,166,0.08)"
           : isLoser         ? "rgba(255,255,255,0.015)"
           : picked          ? T.tealDim
           : "rgba(255,255,255,0.025)";
  const border = isCorrect   ? "rgba(34,197,94,0.45)"
              : isWrong      ? "rgba(248,113,113,0.30)"
              : isWinner     ? T.borderTeal
              : picked       ? T.borderTeal
              : T.border;

  const showFlag = !!teamCode;

  return (
    <button
      onClick={onTap}
      disabled={disabled}
      className="no-press"
      style={{
        display:"flex", alignItems:"center", gap:12,
        width:"100%", padding:"11px 12px",
        marginBottom:6,
        background: bg,
        border: `1px solid ${border}`,
        borderRadius:11,
        cursor: disabled ? "default" : "pointer",
        opacity: !teamCode ? 0.55 : (isLoser && !isCorrect && !isWrong ? 0.75 : 1),
        textAlign:"left",
        transition:"background 0.15s, border-color 0.15s, transform 0.1s",
      }}
      onTouchStart={(e) => { if (!disabled) e.currentTarget.style.transform="scale(0.985)"; }}
      onTouchEnd={(e)   => { e.currentTarget.style.transform="scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform="scale(1)"; }}
    >
      {showFlag ? (
        <TeamFlag team={t} size={22} style={{ marginRight:0, flexShrink:0 }} />
      ) : (
        <div style={{
          width:22, height:22, flexShrink:0,
          borderRadius:6, background:"rgba(255,255,255,0.04)",
          border:`1px dashed ${T.border}`,
        }}/>
      )}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{
          fontSize:14, fontWeight:700,
          color: teamCode ? T.ink : T.inkMuted,
          letterSpacing:"-0.01em",
          fontStyle: teamCode ? "normal" : "italic",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
        }}>
          {teamCode ? t.name : label}
        </div>
        {teamCode && (
          <div style={{
            fontSize:10, fontWeight:600, color:T.inkMuted,
            letterSpacing:"0.10em", marginTop:1,
          }}>{t.abbr}</div>
        )}
      </div>

      {/* Right-side affordance: score (if played), check (if picked), or
          "Tap to pick" hint. Result + picked combine into a single check
          icon. */}
      {score != null ? (
        <div style={{
          fontFamily:FM, fontSize:18, fontWeight:700,
          color: isWinner ? T.ink : T.inkSec,
          letterSpacing:"-0.02em", minWidth:24, textAlign:"right",
        }}>{score}</div>
      ) : picked ? (
        <div style={{
          display:"flex", alignItems:"center", gap:5,
          fontSize:10, fontWeight:800,
          color: isCorrect ? "#22c55e" : isWrong ? "#f87171" : T.teal,
          letterSpacing:"0.10em", textTransform:"uppercase",
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke={isCorrect ? "#22c55e" : isWrong ? "#f87171" : T.teal}
            strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          Picked
        </div>
      ) : teamCode && !disabled ? (
        <div style={{
          fontSize:10, fontWeight:700, color:T.inkFaint,
          letterSpacing:"0.10em", textTransform:"uppercase",
        }}>Tap to pick</div>
      ) : null}
    </button>
  );
}

// ── Knockout Match Card ──────────────────────────────────────────────────
//
// One bracket slot rendered as a small card with two team rows. State
// drives the entire UI:
//   - PRE-DATA: home and/or away not yet resolvable. Rows show position
//               labels ("Winner Group A", "3rd of A/B/C/D/F") disabled.
//   - PICKABLE: both sides known, match not yet played. Tap to pick winner;
//               tap again to unpick; tap other team to swap.
//   - PLAYED:   liveStandings carries a winnerCode for this slot. Rows
//               show scores; winner row tinted, user's pick gets a
//               correctness color, status pill shows "Final · PK/AET".
//
// Tap-to-swap semantics: tapping a team that's not currently picked sets
// it as the pick (replacing any previous pick on this slot). Tapping the
// currently-picked team unpicks (clears the slot).
function KnockoutMatchCard({ round, slotDef, predictions, liveStandings, onPick }) {
  const { home, away } = useMemo(
    () => getMatchTeams(round, slotDef.slot, liveStandings || {}),
    [round, slotDef.slot, liveStandings]
  );

  const liveMatch = useMemo(() => {
    const arr = liveStandings?.knockouts?.[round];
    if (!Array.isArray(arr)) return null;
    return arr.find(m => m && m.matchSlot === slotDef.slot) || null;
  }, [liveStandings, round, slotDef.slot]);

  const userPick = predictions?.knockouts?.[round]?.[slotDef.slot] || null;
  const matchPlayed = !!(liveMatch && liveMatch.winnerCode);
  const bothResolved = !!(home && away);
  const isPickable = bothResolved && !matchPlayed;

  const homeLabel = home ? null : formatPositionLabel(slotDef.home);
  const awayLabel = away ? null : formatPositionLabel(slotDef.away);

  const homeIsWinner = matchPlayed && liveMatch.winnerCode === home;
  const awayIsWinner = matchPlayed && liveMatch.winnerCode === away;
  const userPickCorrect = matchPlayed && !!userPick && userPick === liveMatch.winnerCode;
  const userPickWrong   = matchPlayed && !!userPick && userPick !== liveMatch.winnerCode;

  // Status pill copy. Pre-data calls it out so users know why the card is
  // disabled. Picked + not played shows the pick state. Played shows the
  // result modifier (PK / AET) if any.
  let pillLabel = null, pillColor = T.inkMuted, pillBg = "rgba(255,255,255,0.04)";
  if (matchPlayed) {
    const suffix = liveMatch.decidedBy === "penalties" ? " · PK"
                 : liveMatch.decidedBy === "extra_time" ? " · AET"
                 : "";
    pillLabel = `Final${suffix}`;
    pillColor = T.ink; pillBg = "rgba(255,255,255,0.06)";
  } else if (!bothResolved) {
    pillLabel = "Awaiting matchup";
    pillColor = T.gold; pillBg = "rgba(229,180,89,0.08)";
  } else if (userPick) {
    pillLabel = "Picked";
    pillColor = T.teal; pillBg = "rgba(20,184,166,0.10)";
  } else {
    pillLabel = "Open";
    pillColor = T.teal; pillBg = "rgba(20,184,166,0.06)";
  }

  return (
    <div style={{
      background: T.card,
      border: `1px solid ${userPick ? T.borderTeal : T.border}`,
      borderRadius: 13,
      padding: "10px 12px 8px",
      marginBottom: 8,
      transition: "border-color 0.2s",
    }}>
      {/* Caption row: match number + venue · status pill */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        gap:8, marginBottom:8,
      }}>
        <div style={{
          fontFamily: FM, fontSize: 10, fontWeight: 700,
          color: T.inkMuted, letterSpacing: "0.10em",
          whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
          minWidth:0, flex:1,
        }}>
          M{slotDef.matchNumber} · <span style={{ textTransform:"uppercase" }}>{slotDef.venue}</span>
        </div>
        <div style={{
          flexShrink:0,
          padding:"3px 8px", borderRadius:999,
          background: pillBg,
          fontSize: 9.5, fontWeight: 800, color: pillColor,
          letterSpacing: "0.10em", textTransform: "uppercase",
          whiteSpace:"nowrap",
        }}>{pillLabel}</div>
      </div>

      {/* Home row */}
      <KnockoutMatchTeamRow
        teamCode={home}
        label={homeLabel}
        score={matchPlayed ? liveMatch.homeScore : null}
        picked={userPick === home && !matchPlayed}
        isWinner={homeIsWinner}
        isLoser={matchPlayed && !homeIsWinner}
        correctness={userPick === home && matchPlayed
          ? (userPickCorrect ? "correct" : userPickWrong ? "wrong" : null)
          : null}
        disabled={!isPickable}
        onTap={() => isPickable && onPick(slotDef.slot, home)}
      />

      {/* Away row */}
      <KnockoutMatchTeamRow
        teamCode={away}
        label={awayLabel}
        score={matchPlayed ? liveMatch.awayScore : null}
        picked={userPick === away && !matchPlayed}
        isWinner={awayIsWinner}
        isLoser={matchPlayed && !awayIsWinner}
        correctness={userPick === away && matchPlayed
          ? (userPickCorrect ? "correct" : userPickWrong ? "wrong" : null)
          : null}
        disabled={!isPickable}
        onTap={() => isPickable && onPick(slotDef.slot, away)}
      />
    </div>
  );
}

// ── KnockoutsView (Phase 2B real picking) ───────────────────────────────
//
// Per-match pick cards for each round of the knockout bracket. Rounds open
// progressively (date-gated for v1, will move to per-match gates once the
// live cron is the canonical source of truth). Locked rounds keep their
// pre-Phase-2B placeholder card so users see the countdown; unlocked rounds
// expand into 1-16 individual match cards.
//
// Pick storage shape:
//   predictions.knockouts.R32 = { "R32-1": "MEX", "R32-2": "BRA", ... }
//
// Keyed by matchSlot string (not array index) so the scoring code in
// worldCup2026Scoring.js can read picks back without an ordering assumption.
function KnockoutsView({ predictions, onUpdate }) {
  const now = new Date();
  // Re-tick every minute so countdown labels stay fresh without a full
  // re-render. Cheap; bumps a no-op state value.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick(n => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Live standings — used to resolve actual matchups (1A → MEX) AND to
  // render played-match results. Pre-tournament this is null and every
  // card shows the pre-data placeholder.
  const liveStandings = useWorldCupLiveStandings();
  const liveData = useMemo(
    () => computeKnockoutScore(predictions, liveStandings),
    [predictions, liveStandings]
  );
  const showLive = hasAnyKnockoutData(liveStandings);
  const lastUpdated = liveStandings?.lastUpdated;
  const lastUpdatedLabel = formatLastUpdatedAgo(lastUpdated);

  // Knockout max for the header pill: 80 + 120 + 120 + 120 + 200 = 640
  const knockoutMax = ROUND_ORDER.reduce(
    (sum, r) => sum + ROUND_SLOTS[r] * ROUND_PTS[r], 0
  );

  // Pick / unpick handler. Tap a team to pick it; tap the same team again
  // to unpick; tap the other team to swap (replacing the previous pick).
  const setPick = useCallback((round, slot, teamCode) => {
    if (!getRoundUnlockStatus(round, new Date()).isUnlocked) return;
    const currentRoundRaw = predictions?.knockouts?.[round];
    const currentRound = (currentRoundRaw && typeof currentRoundRaw === "object")
      ? currentRoundRaw : {};
    const nextRound = { ...currentRound };
    if (teamCode === null || teamCode === currentRound[slot]) {
      delete nextRound[slot];
    } else {
      nextRound[slot] = teamCode;
    }
    tap();
    onUpdate({
      ...predictions,
      knockouts: {
        ...(predictions.knockouts || {}),
        [round]: nextRound,
      },
    });
  }, [predictions, onUpdate]);

  return (
    <div style={{ padding:"4px 4px 16px" }}>
      {/* Top explainer — sets expectations before the round list so users
          understand WHY they can't pick locked rounds yet. */}
      <div style={{
        marginBottom:14, padding:"14px 14px",
        background:"rgba(20,184,166,0.05)",
        border:`1px solid ${T.borderTeal}`,
        borderRadius:13,
      }}>
        <div style={{
          fontSize:9.5, fontWeight:800, color:T.teal,
          letterSpacing:"0.16em", textTransform:"uppercase",
          marginBottom:6,
        }}>How knockouts work</div>
        <div style={{ fontSize:12, color:T.inkSec, lineHeight:1.5 }}>
          Each round opens as the previous round finishes — you'll pick winners against the <em>actual</em> matchups, not predicted ones. Tap a team to pick the winner; tap again to swap. Round of 32 unlocks the day after group stage ends.
        </div>
      </div>

      {/* Live-score pill — only when actual KO matches have been played. */}
      {showLive && (
        <div style={{
          marginBottom:14, padding:"14px 14px",
          background:"rgba(20,184,166,0.06)",
          border:`1px solid ${T.borderTeal}`,
          borderRadius:13,
          display:"flex", alignItems:"center", gap:14,
        }}>
          <div style={{
            width:34, height:34, borderRadius:11, flexShrink:0,
            background:"rgba(20,184,166,0.12)",
            border:`1px solid ${T.borderTeal}`,
            display:"flex", alignItems:"center", justifyContent:"center",
            position:"relative",
          }}>
            <span style={{
              width:8, height:8, borderRadius:"50%",
              background:T.teal, display:"block",
              boxShadow:`0 0 0 0 ${T.teal}55`,
              animation:"wcLivePulse 1.6s ease-out infinite",
            }}/>
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{
              fontSize:9.5, fontWeight:800, color:T.teal,
              letterSpacing:"0.16em", textTransform:"uppercase",
              marginBottom:3,
            }}>
              {liveData.isFullyComplete ? "Knockouts final" : "Live · projected"}
            </div>
            <div style={{
              fontFamily:FD, fontSize:18, fontWeight:700,
              color:T.ink, letterSpacing:"-0.01em", lineHeight:1.1,
              marginBottom:2,
            }}>
              {liveData.totalPoints} <span style={{ color:T.inkSec, fontSize:13, fontWeight:600 }}>/ {knockoutMax} pts</span>
            </div>
            <div style={{ fontSize:10.5, color:T.inkMuted }}>
              {liveData.matchesCompleted} {liveData.matchesCompleted === 1 ? "match" : "matches"} played{lastUpdatedLabel ? ` · updated ${lastUpdatedLabel}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Round sections */}
      {ROUND_ORDER.map((round) => {
        const status = getRoundUnlockStatus(round, now);
        const points = ROUND_PTS[round];
        const rawStored = predictions?.knockouts?.[round];
        const storedObj = (rawStored && typeof rawStored === "object") ? rawStored : {};
        // Required slots = every match in the round EXCEPT ones that already
        // finished before the user could pick them. A match that kicked off on
        // unlock day can't be picked by anyone, so it must drop out of the
        // denominator — otherwise the round is stuck at e.g. 15/16 forever.
        const liveKoArr = Array.isArray(liveStandings?.knockouts?.[round]) ? liveStandings.knockouts[round] : [];
        const finishedSlots = new Set(liveKoArr.filter(m => m && m.matchSlot && m.winnerCode).map(m => m.matchSlot));
        const allSlotDefs = KNOCKOUT_MATCHUPS[round] || [];
        const requiredSlotDefs = allSlotDefs.filter(sd => {
          const userPicked = isValidTeamCode(storedObj[sd.slot]);
          return userPicked || !finishedSlots.has(sd.slot);
        });
        const slots  = requiredSlotDefs.length || ROUND_SLOTS[round];
        const picked = requiredSlotDefs.filter(sd => isValidTeamCode(storedObj[sd.slot])).length;
        const allDone = status.isUnlocked && picked === slots;

        const accent = status.isUnlocked ? T.teal : T.gold;
        const accentDim = status.isUnlocked
          ? "rgba(20,184,166,0.06)"
          : "rgba(229,180,89,0.04)";
        const accentBorder = status.isUnlocked
          ? T.borderTeal
          : "rgba(229,180,89,0.20)";

        return (
          <div
            key={round}
            id={`ko-round-${round}`}
            style={{
              position:"relative",
              marginBottom:14, padding:"14px 14px",
              background:accentDim,
              border:`1px solid ${accentBorder}`,
              borderRadius:14,
            }}
          >
            {/* Round header */}
            <div style={{
              display:"flex", alignItems:"center", gap:10,
              marginBottom: status.isUnlocked ? 12 : 10,
            }}>
              <div style={{
                width:32, height:32, borderRadius:10, flexShrink:0,
                background:"rgba(0,0,0,0.30)",
                border:`1px solid ${accentBorder}`,
                display:"flex", alignItems:"center", justifyContent:"center",
              }}>
                {status.isUnlocked ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0 1 9.9-1"/>
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                )}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{
                  fontFamily:FD, fontSize:15, fontWeight:700, color:T.ink,
                  letterSpacing:"-0.01em", lineHeight:1.1,
                }}>{ROUND_LABELS[round]}</div>
                <div style={{
                  fontSize:10.5, color:T.inkSec, marginTop:2,
                }}>
                  {slots} {slots === 1 ? "match" : "matches"} · {points} pts each
                </div>
              </div>
              <div style={{
                flexShrink:0,
                padding:"3px 9px", borderRadius:999,
                background:"rgba(0,0,0,0.30)",
                border:`1px solid ${accentBorder}`,
                fontSize:9.5, fontWeight:800, color:accent,
                letterSpacing:"0.10em", textTransform:"uppercase",
                whiteSpace:"nowrap",
              }}>
                {status.isUnlocked
                  ? (allDone ? `${picked}/${slots} ✓` : `${picked}/${slots}`)
                  : status.label}
              </div>
            </div>

            {/* Locked round body — countdown copy */}
            {!status.isUnlocked && (
              <div style={{
                fontSize:11.5, color:T.inkSec, lineHeight:1.5,
                paddingLeft:42,
              }}>
                {round === "R32"
                  ? <>Real matchups set after group stage. Unlocks <strong style={{color:T.ink}}>{status.dateLabel}</strong>.</>
                  : <>Unlocks <strong style={{color:T.ink}}>{status.dateLabel}</strong> when {ROUND_LABELS[ROUND_ORDER[ROUND_ORDER.indexOf(round) - 1]]} finishes.</>
                }
              </div>
            )}

            {/* Unlocked round body — per-match pick cards. */}
            {status.isUnlocked && (
              <div>
                {(KNOCKOUT_MATCHUPS[round] || []).map((slotDef) => (
                  <KnockoutMatchCard
                    key={slotDef.slot}
                    round={round}
                    slotDef={slotDef}
                    predictions={predictions}
                    liveStandings={liveStandings}
                    onPick={(slot, code) => setPick(round, slot, code)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Golden Boot View ────────────────────────────────────────────────────
// Free-text input for the player you predict will score the most goals.
// We don't have player rosters in worldCup2026Config — and FIFA hasn't
// confirmed final 2026 squads — so a typed name is the right interface.
// A future enhancement could pull a curated player list from a separate
// config and offer autocomplete; for v1 the typed name is graded manually
// or via fuzzy match server-side post-tournament.
function GoldenBootView({ predictions, onUpdate, locked = false }) {
  const [draft, setDraft] = useState(predictions?.goldenBoot || "");

  // Debounce save — same pattern as group autosave (handled at parent level).
  useEffect(() => {
    if (locked) return;
    if (draft === (predictions?.goldenBoot || "")) return;
    const id = setTimeout(() => {
      onUpdate({ ...predictions, goldenBoot: draft.trim() });
    }, 600);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, locked]);

  return (
    <div>
      <div style={{ padding:"4px 4px 14px" }}>
        <div style={{
          fontFamily:FD, fontSize:24, fontWeight:700,
          color:T.ink, letterSpacing:"-0.025em",
          marginBottom:6, lineHeight:1.1,
        }}>
          ⚽ Golden Boot
        </div>
        <div style={{ fontSize:12, color:T.inkSec, lineHeight:1.5 }}>
          Who scores the most goals in the entire tournament? {SCORING.GOLDEN_BOOT} pts on the line.
        </div>
      </div>

      {locked && (
        <div style={{
          display:"flex", gap:10, alignItems:"flex-start",
          padding:"12px 14px", marginBottom:14,
          background:"rgba(199,101,97,0.08)",
          border:"1px solid rgba(199,101,97,0.22)",
          borderRadius:12,
        }}>
          <span style={{ fontSize:15, lineHeight:1.3 }}>🔒</span>
          <div style={{ fontSize:12.5, color:"#E89B97", lineHeight:1.5 }}>
            Your Golden Boot pick was locked when the tournament kicked off and
            can't be changed now. Like your group standings and tiebreaker, it
            had to be set before the opening match — that keeps it fair, since
            nobody can adjust their pick after seeing who's scoring.
          </div>
        </div>
      )}

      <div style={{
        padding:"16px",
        background: T.card,
        border: `1px solid ${draft.trim() ? T.borderTeal : T.border}`,
        borderRadius:14,
        marginBottom:16,
        transition:"border-color 0.2s",
        opacity: locked ? 0.55 : 1,
      }}>
        <div style={{
          fontSize:10, fontWeight:700, color:T.inkMuted,
          letterSpacing:"0.10em", textTransform:"uppercase",
          marginBottom:8,
        }}>Player Name</div>
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={locked ? "No Golden Boot pick was set before kickoff" : "e.g. Lionel Messi, Erling Haaland, Kylian Mbappé"}
          maxLength={60}
          disabled={locked}
          style={{
            width:"100%", padding:"12px 14px",
            background:"rgba(255,255,255,0.03)",
            border:`1px solid ${T.border}`,
            borderRadius:11,
            fontSize:15, fontWeight:600,
            color:T.ink, fontFamily:FB,
            outline:"none", boxSizing:"border-box",
            cursor: locked ? "not-allowed" : "text",
          }}
        />
        <div style={{
          fontSize:11, color:T.inkMuted, marginTop:8, lineHeight:1.5,
        }}>
          {locked
            ? `This pick is final for the rest of the tournament (${SCORING.GOLDEN_BOOT} pts if correct).`
            : `Type any active player. Spelling close enough wins (${SCORING.GOLDEN_BOOT} pts).`}
        </div>
      </div>
    </div>
  );
}

// ── Tiebreaker View ────────────────────────────────────────────────────
// Total goals scored in the FINAL match. Used to break league-leader ties
// at the end of the tournament. Number input, 0-15 range (most finals fall
// 0-7; 15 is a safe cap that prevents typos like 100).
function TiebreakerView({ predictions, onUpdate, locked = false }) {
  const stored = Number(predictions?.totalGoals);
  const [draft, setDraft] = useState(Number.isFinite(stored) && stored > 0 ? String(stored) : "");

  useEffect(() => {
    if (locked) return;
    const n = Math.floor(Number(draft));
    if (!Number.isFinite(n) || n <= 0) {
      // Empty / invalid input — clear the stored value if it was set.
      // Push update IMMEDIATELY (no local debounce); parent's scheduleSave
      // already batches Firestore writes with its own 1000ms debounce. Stacking
      // local + parent debounces created a 1.6s race window where clicking
      // close before the timer fired silently dropped the save (line 2083
      // cleanup clears the pending timer on unmount).
      if (predictions?.totalGoals != null && draft === "") {
        onUpdate({ ...predictions, totalGoals: null });
      }
      return;
    }
    if (n === predictions?.totalGoals) return;
    onUpdate({ ...predictions, totalGoals: n });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, locked]);

  return (
    <div>
      <div style={{ padding:"4px 4px 14px" }}>
        <div style={{
          fontFamily:FD, fontSize:24, fontWeight:700,
          color:T.ink, letterSpacing:"-0.025em",
          marginBottom:6, lineHeight:1.1,
        }}>
          🎯 Tiebreaker
        </div>
        <div style={{ fontSize:12, color:T.inkSec, lineHeight:1.5 }}>
          Total goals scored in the Final. If you're tied with another player at the end of the tournament, closest guess wins.
        </div>
      </div>

      <div style={{
        padding:"16px",
        background: T.card,
        border: `1px solid ${draft.trim() && Number(draft) > 0 ? T.borderTeal : T.border}`,
        borderRadius:14,
        marginBottom:16,
        transition:"border-color 0.2s",
        opacity: locked ? 0.55 : 1,
      }}>
        <div style={{
          fontSize:10, fontWeight:700, color:T.inkMuted,
          letterSpacing:"0.10em", textTransform:"uppercase",
          marginBottom:8,
        }}>Total Goals (Both Teams)</div>
        <input
          type="number"
          inputMode="numeric"
          pattern="[0-9]*"
          min={1} max={15} step={1}
          value={draft}
          disabled={locked}
          onChange={(e) => {
            const raw = (e.target.value || "").replace(/[^\d]/g, "");
            if (raw === "") { setDraft(""); return; }
            const n = Math.floor(Number(raw));
            if (!Number.isFinite(n) || n < 0) { setDraft(""); return; }
            setDraft(String(Math.min(n, 15)));
          }}
          placeholder="e.g. 3"
          style={{
            width:"100%", padding:"16px 14px",
            background:"rgba(255,255,255,0.03)",
            border:`1px solid ${T.border}`,
            borderRadius:11,
            fontSize:28, fontWeight:700,
            color:T.ink, fontFamily:FM,
            textAlign:"center", letterSpacing:"-0.01em",
            outline:"none", boxSizing:"border-box",
            WebkitAppearance:"none", MozAppearance:"textfield",
            cursor: locked ? "not-allowed" : "text",
          }}
        />
        <div style={{
          fontSize:11, color:T.inkMuted, marginTop:8, lineHeight:1.5, textAlign:"center",
        }}>
          Most finals end 1–4 goals. Last 6 World Cup finals: 3, 6, 1, 3, 4, 5.
        </div>
      </div>
    </div>
  );
}

// ── Top header with progress + close ────────────────────────────────────
function BracketHeader({ leagueName, progress, onClose }) {
  return (
    <div style={{
      position:"sticky", top:0, zIndex:5,
      background:T.bg,
      borderBottom:`1px solid ${T.border}`,
      padding:"calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            width:32, height:32, borderRadius:10,
            background:"rgba(255,255,255,0.04)",
            border:`1px solid ${T.border}`,
            color:T.ink, fontSize:18, lineHeight:1,
            cursor:"pointer", flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"center",
          }}
        >×</button>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:2 }}>
            <TrophyMark size={14}/>
            <span style={{
              fontSize:9, fontWeight:700, color:T.gold,
              letterSpacing:"0.16em", textTransform:"uppercase",
            }}>World Cup 2026</span>
          </div>
          <div style={{
            fontSize:14, fontWeight:700, color:T.ink,
            letterSpacing:"-0.01em",
            whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
          }}>{leagueName || "Bracket"}</div>
        </div>
        <div style={{
          fontFamily:FM, fontSize:11, fontWeight:700, color:T.inkSec,
          flexShrink:0, textAlign:"right",
        }}>
          {progress.completed}/{progress.total}
        </div>
      </div>

      {/* Progress bar */}
      <div style={{
        height:3, marginTop:10,
        background:"rgba(255,255,255,0.04)", borderRadius:3, overflow:"hidden",
      }}>
        <div style={{
          height:"100%", width:`${Math.round(progress.pct * 100)}%`,
          background:T.teal,
          transition:"width 0.4s cubic-bezier(0.16,1,0.3,1)",
        }}/>
      </div>
    </div>
  );
}

// ── Bottom action bar ───────────────────────────────────────────────────
function BracketFooter({ view, progress, onPrimary, primaryLabel, primaryDisabled }) {
  return (
    <div style={{
      position:"sticky", bottom:0, zIndex:5,
      background:`linear-gradient(180deg, transparent, ${T.bg} 30%)`,
      padding:"16px 16px calc(env(safe-area-inset-bottom, 0px) + 16px)",
    }}>
      <button
        onClick={onPrimary}
        disabled={primaryDisabled}
        style={{
          width:"100%", padding:"14px",
          borderRadius:13, border:"none",
          background: primaryDisabled
            ? "rgba(20,184,166,0.20)"
            : T.teal,
          color:"#fff", fontSize:14, fontWeight:700,
          letterSpacing:"0.01em",
          cursor: primaryDisabled ? "not-allowed" : "pointer",
          boxShadow: primaryDisabled ? "none" : "0 4px 14px rgba(20,184,166,0.30)",
          transition:"opacity 0.15s",
        }}
      >{primaryLabel}</button>
    </div>
  );
}

// ── Top-level overlay component ─────────────────────────────────────────
export default function WorldCupBracket({
  user,
  username,
  league,
  onClose,
}) {
  const leagueId = league?.id;
  const userId = user?.uid;

  const [predictions, setPredictions] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [view, setView]               = useState("groups"); // groups | knockouts | tiebreaker | summary
  const [savingState, setSavingState] = useState("idle");   // idle | saving | saved | error
  const [shareOpen, setShareOpen]     = useState(false);   // bracket-share modal visibility

  // Live standings drive matchup resolution AND completion math — a knockout
  // match that already finished before its round was pickable must not count
  // against the user's progress (see knockoutProgress).
  const liveStandings = useWorldCupLiveStandings();

  const saveTimerRef = useRef(null);
  // Holds the most recent predictions awaiting Firestore write — lets us
  // flush a pending debounced save synchronously on close. Without this,
  // unmounting the bracket overlay (line 2083 cleanup) cancels the timer
  // and the last keystroke (typically tiebreaker) is silently dropped.
  const pendingSaveRef = useRef(null);

  // Initial load
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const p = await loadPredictions(leagueId, userId);
      if (!alive) return;
      setPredictions(p || emptyPredictions());
      setLoading(false);
    })();
    return () => { alive = false; if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, userId]);

  // ── BottomNav coordination ─────────────────────────────────────────────
  // TorchApp's BottomNav reads body.modal-open and slides itself off-screen
  // when modals are open. Without this, the nav (z-index:40) overlays the
  // bracket's sticky footer button and the user can't see "Locked In · Back
  // to League" — same root cause as the WorldCupBanner create-sheet bug.
  // The bracket overlay always renders when this component mounts, so we
  // toggle the class for the lifetime of the component.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => { document.body.classList.remove("modal-open"); };
  }, []);

  // Debounced autosave with auto-retry. A first attempt fires 1s after the
  // latest change. If it fails (network blip, rules race, etc.) the function
  // self-schedules up to 2 more attempts with 3s and 4s backoff so a transient
  // error doesn't silently lose the user's picks.
  const scheduleSave = useCallback((next) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    pendingSaveRef.current = next;
    setSavingState("saving");

    const attempt = (retryCount) => {
      const delay = retryCount === 0 ? 1000 : 2000 + retryCount * 1000;
      saveTimerRef.current = setTimeout(async () => {
        const ok = await savePredictions(leagueId, userId, username, next);
        if (ok) {
          pendingSaveRef.current = null;
          setSavingState("saved");
          setTimeout(() => setSavingState("idle"), 1400);
        } else {
          setSavingState("error");
          if (retryCount < 2) {
            attempt(retryCount + 1);
          }
        }
      }, delay);
    };

    attempt(0);
  }, [leagueId, userId, username]);

  // Flush any pending debounced save NOW. Called on close so the last
  // keystroke (commonly the tiebreaker) doesn't get dropped by the
  // unmount cleanup at line 2083. Single attempt, no retry — if the
  // network is down the user will re-enter the bracket and the local
  // state will reconcile from Firestore on reload anyway.
  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const pending = pendingSaveRef.current;
    if (!pending) return true;
    setSavingState("saving");
    try {
      const ok = await savePredictions(leagueId, userId, username, pending);
      if (ok) {
        pendingSaveRef.current = null;
        setSavingState("saved");
        setTimeout(() => setSavingState("idle"), 1400);
      } else {
        setSavingState("error");
      }
      return ok;
    } catch (e) {
      setSavingState("error");
      return false;
    }
  }, [leagueId, userId, username]);

  // Close wrapper — guarantees the latest predictions are written to
  // Firestore before the parent unmounts this component. Async so the
  // awaited write resolves before bracketOpen flips to null and TorchApp
  // refetches predictions for the CTA label ("View My Bracket" vs.
  // "Continue Picking · 12/12").
  const closeWithFlush = useCallback(async () => {
    await flushSave();
    onClose();
  }, [flushSave, onClose]);

  const updatePredictions = (next) => {
    // ⚠️ Final safety net. Pre-kickoff: everything is editable. Post-kickoff:
    // group/boot/tiebreaker cards are frozen via their own `locked` props (so
    // their taps are no-ops before reaching here), while knockout rounds stay
    // live as they unlock. We therefore allow the mutation whenever ANYTHING is
    // writable; the per-section `locked` pipeline + the Firestore rule are what
    // actually keep the frozen fields immutable.
    if (!predictionsWritable()) return;
    setPredictions(next);
    scheduleSave(next);
  };

  const progress = predictions ? overallProgress(predictions, new Date(), liveStandings) : { completed:0, total:12, pct:0 };
  // groupsDone reflects ONLY the group stage (12/12). The wider `progress`
  // covers knockouts + golden boot + tiebreaker too, so it can't double as
  // a group-completion flag in Phase 2B.
  const groupsDone = predictions ? groupStageProgress(predictions).pct === 1 : false;

  // Auto-route: when the user opens the bracket with all groups already locked
  // and a knockout round is live, jump straight to the Knockouts view. Groups
  // are frozen at this point, so landing on them is a dead end — the user came
  // to make knockout picks. Only fires once per open so we don't fight a user
  // who deliberately taps back to review their groups.
  const didAutoRouteRef = useRef(false);
  useEffect(() => {
    if (loading || !predictions) return;
    if (didAutoRouteRef.current) return;
    if (groupsDone && view === "groups" && knockoutPicksOpen()) {
      didAutoRouteRef.current = true;
      setView("knockouts");
    } else if (groupsDone && view === "groups" && !predictions.__seenComplete) {
      didAutoRouteRef.current = true;
      setPredictions((p) => ({ ...p, __seenComplete: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Completion celebration — fire a toast + haptic the moment the 12th group
  // gets locked, but NOT on initial load if the user already had everything
  // locked. We capture the initial state once and only celebrate transitions
  // that happen during the current session.
  const initialGroupsDoneRef = useRef(null);
  useEffect(() => {
    if (loading || !predictions) return;
    if (initialGroupsDoneRef.current === null) {
      initialGroupsDoneRef.current = groupsDone;
      return;
    }
    if (groupsDone && !initialGroupsDoneRef.current) {
      initialGroupsDoneRef.current = true; // never fire again this session
      window.torchToast?.("Groups locked. Knockout time. 🏆", "success");
      try { window.navigator.vibrate?.([15, 30, 15]); } catch {}
    }
  }, [groupsDone, loading, predictions]);

  // Full bracket celebration — fires once when EVERY section is locked in
  // during the current session. Same one-shot semantics as the groups toast.
  const initialAllDoneRef = useRef(null);
  useEffect(() => {
    if (loading || !predictions) return;
    const allDone = overallProgress(predictions, new Date(), liveStandings).pct === 1;
    if (initialAllDoneRef.current === null) {
      initialAllDoneRef.current = allDone;
      return;
    }
    if (allDone && !initialAllDoneRef.current) {
      initialAllDoneRef.current = true;
      window.torchToast?.("Bracket locked in. See you July 19. 🏆", "success");
      try { window.navigator.vibrate?.([20, 30, 20, 30, 60]); } catch {}
    }
  }, [loading, predictions, liveStandings]);

  // ── Footer state per view ──────────────────────────────────────────────
  // Progression model: each section has its own footer button that either
  //   (a) shows progress while the section is incomplete (disabled), or
  //   (b) advances to the NEXT section once the current one is complete.
  // The very last section (whichever is the user's final unfinished one)
  // closes the bracket overlay when the user fills it in.
  //
  // Phase 1: Knockouts pre-tournament are entirely locked — the footer for
  // that view becomes a pass-through ("Continue to Golden Boot →") instead
  // of waiting on picks that can't be made yet. The phase-aware
  // `knockoutProgress(predictions)` returns total=0 when no rounds have
  // unlocked, so the math for `allDone` skips knockouts cleanly.
  const footerCfg = useMemo(() => {
    const groupProg = groupStageProgress(predictions);
    const koProg    = knockoutProgress(predictions, new Date(), liveStandings);
    const gbDone    = goldenBootDone(predictions);
    const tbDone    = tiebreakerDone(predictions);
    // Pre-tournament: koProg.total === 0 and koProg.pct === 1, so `allDone`
    // fires on groups + boot + tiebreaker. During tournament: koProg.total
    // expands to include unlocked rounds and must be fully picked.
    const allDone   = groupProg.pct === 1 && koProg.pct === 1 && gbDone && tbDone;

    if (allDone) {
      return {
        label: "✓ Bracket Locked In · Back to League",
        disabled: false,
        onClick: closeWithFlush,
      };
    }

    if (view === "groups") {
      if (groupProg.pct < 1) {
        return { label: `Groups — ${groupProg.completed}/12 done`, disabled: true, onClick: () => {} };
      }
      return { label: "Continue to Knockouts →", disabled: false, onClick: () => { tap(); setView("knockouts"); } };
    }
    if (view === "knockouts") {
      if (groupProg.pct < 1) {
        return { label: "Lock all 12 groups first", disabled: true, onClick: () => {} };
      }
      // Pre-tournament: knockouts are locked, so let the user move forward
      // to Golden Boot rather than getting stuck on a section with nothing
      // to pick. During tournament: enforce the pick count for unlocked rounds.
      if (koProg.total === 0) {
        return { label: "Continue to Golden Boot →", disabled: false, onClick: () => { tap(); setView("goldenBoot"); } };
      }
      if (koProg.pct < 1) {
        return { label: `Knockouts — ${koProg.completed}/${koProg.total} done`, disabled: true, onClick: () => {} };
      }
      return { label: "Pick Golden Boot →", disabled: false, onClick: () => { tap(); setView("goldenBoot"); } };
    }
    if (view === "goldenBoot") {
      if (!gbDone) {
        return { label: "Type a player name", disabled: true, onClick: () => {} };
      }
      return { label: "Set Tiebreaker →", disabled: false, onClick: () => { tap(); setView("tiebreaker"); } };
    }
    if (view === "tiebreaker") {
      if (!tbDone) {
        return { label: "Predict goals in the final", disabled: true, onClick: () => {} };
      }
      // Tiebreaker is the last section — if the user's filled it but earlier
      // sections aren't done, send them back to fix the gaps. Otherwise the
      // allDone branch above handles the close. (Knockouts skipped pre-tournament.)
      return { label: "Review missing picks", disabled: false, onClick: () => {
        tap();
        if (groupProg.pct < 1) setView("groups");
        else if (koProg.total > 0 && koProg.pct < 1) setView("knockouts");
        else setView("goldenBoot");
      }};
    }
    return { label: "Done", disabled: false, onClick: closeWithFlush };
  }, [view, predictions, onClose, closeWithFlush, liveStandings]);

  // Lock indicator (predictions still open?)
  const stillOpen = predictionsOpen();
  // Knockouts have their OWN lock lifecycle: they're frozen pre-tournament and
  // open progressively as each round unlocks during the tournament — the
  // opposite phase from groups (which lock AT kickoff). So the knockout view
  // must NOT inherit the group lock; it's editable whenever any KO round is
  // open. Per-round/per-card gating (getRoundUnlockStatus + getMatchTeams)
  // still controls which individual matches are pickable.
  const knockoutsLocked = !knockoutPicksOpen();

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div style={{
      position:"fixed", inset:0, zIndex:9999,
      background:T.bg,
      display:"flex", flexDirection:"column",
      animation:"wcBracketIn 0.22s ease-out both",
    }}>
      {/* Inline keyframes — local to this component */}
      <style>{`
        @keyframes wcBracketIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes wcSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      <BracketHeader
        leagueName={league?.name}
        progress={progress}
        onClose={closeWithFlush}
      />

      {/* Save status pill — small, non-intrusive */}
      {savingState !== "idle" && (
        <div style={{
          position:"absolute", top:"calc(env(safe-area-inset-top, 0px) + 64px)",
          right:14, zIndex:6,
          padding:"5px 10px", borderRadius:999,
          background: savingState === "error" ? "rgba(199,101,97,0.18)" : T.tealDim,
          border: `1px solid ${savingState === "error" ? "rgba(199,101,97,0.40)" : T.borderTeal}`,
          fontSize:10, fontWeight:700, color: savingState === "error" ? "#E89B97" : T.teal,
          letterSpacing:"0.10em", textTransform:"uppercase",
          fontFamily:FM,
          opacity: 0.95,
        }}>
          {savingState === "saving" ? "Saving…" : savingState === "saved" ? "Saved" : "Save failed"}
        </div>
      )}

      {/* Lock notice. Three states:
          • pre-kickoff: nothing shown (everything editable)
          • groups locked, knockouts OPEN: clarify the split so users know they
            CAN still pick knockouts (the old copy wrongly said "locked")
          • everything locked (post-final): the plain locked message */}
      {!stillOpen && (
        <div style={{
          margin:"12px 16px 0",
          padding:"10px 12px",
          background: knockoutsLocked ? "rgba(199,101,97,0.10)" : "rgba(20,184,166,0.08)",
          border: `1px solid ${knockoutsLocked ? "rgba(199,101,97,0.25)" : "rgba(20,184,166,0.22)"}`,
          borderRadius:10,
          fontSize:11, color: knockoutsLocked ? "#E89B97" : T.teal,
        }}>
          {knockoutsLocked
            ? "Predictions are locked — kickoff has passed. Your final picks are shown below."
            : "Group picks are locked, but knockout rounds are open — head to the Knockouts tab to pick winners as each round unlocks."}
        </div>
      )}

      {/* Body — scrollable */}
      <div data-wc-body style={{
        flex:1, overflowY:"auto", WebkitOverflowScrolling:"touch",
        padding:"14px 14px 0",
      }}>
        {loading && (
          <div style={{
            padding:"60px 16px", textAlign:"center",
          }}>
            <div style={{
              width:24, height:24, margin:"0 auto 12px",
              border:`2px solid ${T.border}`,
              borderTopColor:T.teal,
              borderRadius:"50%",
              animation:"wcSpin 0.7s linear infinite",
            }}/>
            <div style={{ color:T.inkMuted, fontSize:12 }}>Loading your bracket…</div>
          </div>
        )}

        {/* Globals — render once across all views so users always see the
            tournament context, can re-read the explainer, get the celebration
            when fully locked in, and can navigate between sections. */}
        {!loading && predictions && (
          <>
            <PhaseTimeline onJumpToPhase={(key) => {
              // Phase keys → app actions:
              //   "pre"    → already in pre-tournament; scroll to top of body
              //   "groups" → switch to Groups tab
              //   r16/qf/sf/final → switch to Knockouts tab + scroll to that
              //     round's card. Note phase key "r16" is timeline shorthand
              //     for the FIRST knockout round (R32 in our system) — but
              //     since the round IDs are R16/QF/SF/FINAL, just uppercase
              //     and scroll. (Phase timeline's R16 label is intentional;
              //     don't redesign in this UI-polish pass.)
              const scrollBodyTop = () => {
                // Find the scrollable body via parent traversal — works
                // regardless of where in the tree we are
                const el = document.querySelector('[data-wc-body]');
                if (el) el.scrollTo({ top: 0, behavior: "smooth" });
              };
              if (key === "pre") {
                scrollBodyTop();
              } else if (key === "groups") {
                setView("groups");
                setTimeout(scrollBodyTop, 50);
              } else {
                setView("knockouts");
                // Wait for the view switch to render the round cards before
                // scrolling. Two ticks of setTimeout = ~32ms is enough
                // for React to commit + paint.
                setTimeout(() => {
                  const id = `ko-round-${key.toUpperCase()}`;
                  const target = document.getElementById(id);
                  if (target) {
                    target.scrollIntoView({ behavior: "smooth", block: "start" });
                  } else {
                    scrollBodyTop();
                  }
                }, 50);
              }
            }} />
            <HowItWorksPanel />
            {/* Share My Bracket hidden for v1 launch — the current canvas image
                doesn't show enough bracket content to be a strong recruit asset,
                and the league invite flow is the better viral mechanic. Canvas
                code below (~lines 904-1300) preserved; re-enable by passing
                onShareBracket back to LockedInHero. */}
            <LockedInHero predictions={predictions} />
            <BracketSectionTabs view={view} setView={setView} predictions={predictions} />
          </>
        )}

        {/* Active view -- `locked` reflects whether predictions can still
            be edited. Each sub-view propagates this to its tap/input
            handlers as a defense-in-depth pass on top of the
            updatePredictions short-circuit. */}
        {!loading && view === "groups" && (
          <GroupStageView predictions={predictions} onUpdate={updatePredictions} locked={!stillOpen} />
        )}
        {!loading && view === "knockouts" && (
          <KnockoutsView predictions={predictions} onUpdate={updatePredictions} groupsDone={groupsDone} locked={knockoutsLocked} />
        )}
        {!loading && view === "goldenBoot" && (
          <GoldenBootView predictions={predictions} onUpdate={updatePredictions} locked={!stillOpen} />
        )}
        {!loading && view === "tiebreaker" && (
          <TiebreakerView predictions={predictions} onUpdate={updatePredictions} locked={!stillOpen} />
        )}

        {/* Spacer so footer doesn't cover bottom of last card */}
        <div style={{ height:24 }}/>
      </div>

      <BracketFooter
        view={view}
        progress={progress}
        onPrimary={footerCfg.onClick}
        primaryLabel={footerCfg.label}
        primaryDisabled={footerCfg.disabled}
      />

      {/* Share bracket modal — overlays everything when triggered from
          LockedInHero. Renders the user's bracket as a Canvas-generated
          PNG and offers Share / Copy / Download actions. */}
      <ShareBracketModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        predictions={predictions}
        username={username}
        leagueName={league?.name || ""}
      />
    </div>
  );
}
